import Phaser from 'phaser';
import { pokerClient } from '../index';
import { PokerTableState, PlayerState, EmojiReactionEvent } from '../network/PokerClient';
import { AudioSettings, loadAudioSettings, onAudioSettingsChanged, saveAudioSettings } from '../utils/audioSettings';
import { DeckId, DeckSettings, TableTheme, loadDeckSettings, saveDeckSettings } from '../utils/deckSettings';
import { AVATAR_PRESET_KEYS, getSelectedAvatarValue, loadAvatarSettings } from '../utils/avatarSettings';

type RequireContext = {
  keys: () => string[];
  (key: string): string;
};

type CardBackAsset = { key: string; url: string };

const buildCardBackAssets = (
  deckId: DeckId,
  context: RequireContext,
): CardBackAsset[] => context.keys().map((key: string) => {
  const name = key.replace('./', '').replace(/\.[^.]+$/, '');
  return { key: `card_back_${deckId}_${name}`, url: context(key) as string };
});

// Load reverse (card back) assets at build time; new files dropped into
// public/assets/Cards*/Reverse will be picked up automatically after rebuild.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const reverseContextClassic = (require as any).context('../../public/assets/Cards2/Reverse', false, /\.(png|jpe?g|webp)$/);
// eslint-disable-next-line @typescript-eslint/no-var-requires
const reverseContextAlternative = (require as any).context('../../public/assets/Cards/Reverse', false, /\.(png|jpe?g|webp)$/);

const CARD_BACK_ASSETS_BY_DECK: Record<DeckId, CardBackAsset[]> = {
  classic: buildCardBackAssets('classic', reverseContextClassic),
  alternative: buildCardBackAssets('alternative', reverseContextAlternative),
};

const CARD_BACK_KEYS_BY_DECK: Record<DeckId, string[]> = {
  classic: CARD_BACK_ASSETS_BY_DECK.classic.map(asset => asset.key),
  alternative: CARD_BACK_ASSETS_BY_DECK.alternative.map(asset => asset.key),
};

type CardFaceAsset = { key: string; url: string; code: string };

const buildCardFaceAssets = (
  deckId: DeckId,
  context: RequireContext,
): CardFaceAsset[] => context.keys()
  .filter((key: string) => !key.includes('/Reverse/') && !key.includes('/_extras/'))
  .map((key: string) => {
    const name = key.replace('./', '').replace(/\.[^.]+$/, '');
    const parts = name.split('/');
    const fileName = parts[parts.length - 1];
    const code = fileName.toLowerCase();
    return { key: `card_face_${deckId}_${code}`, url: context(key) as string, code };
  });

const buildCardFaceMap = (assets: CardFaceAsset[]): Record<string, string> => {
  const map: Record<string, string> = {};
  assets.forEach(({ code, key }) => {
    map[code] = key;
  });
  return map;
};

// Load card face assets from public/assets/Cards2 (classic) and Cards (alternative)
// eslint-disable-next-line @typescript-eslint/no-var-requires
const cardFaceContextClassic = (require as any).context('../../public/assets/Cards2', true, /\.(png|jpe?g|webp)$/);
// eslint-disable-next-line @typescript-eslint/no-var-requires
const cardFaceContextAlternative = (require as any).context('../../public/assets/Cards', true, /\.(png|jpe?g|webp)$/);

const CARD_FACE_ASSETS_BY_DECK: Record<DeckId, CardFaceAsset[]> = {
  classic: buildCardFaceAssets('classic', cardFaceContextClassic),
  alternative: buildCardFaceAssets('alternative', cardFaceContextAlternative),
};

const CARD_FACE_MAPS: Record<DeckId, Record<string, string>> = {
  classic: buildCardFaceMap(CARD_FACE_ASSETS_BY_DECK.classic),
  alternative: buildCardFaceMap(CARD_FACE_ASSETS_BY_DECK.alternative),
};

// Load audio assets from public/assets/Audio at build time.
// New files dropped into public/assets/Audio will be picked up automatically after rebuild.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const audioContext = (require as any).context('../../public/assets/Audio', false, /\.(ogg|mp3|wav)$/);
type AudioAsset = { name: string; key: string; url: string };
const AUDIO_ASSETS: AudioAsset[] = audioContext.keys().map((key: string) => {
  const name = key.replace('./', '').replace(/\.[^.]+$/, '');
  // Use a stable key format; keep dashes/underscores.
  const safeName = name.replace(/[^a-zA-Z0-9_-]/g, '');
  return { name, key: `sfx_${safeName}`, url: audioContext(key) as string };
});

type VolumeSlider = {
  container: Phaser.GameObjects.Container;
  updateFromValue: (value: number) => void;
};

// Pozycje miejsc przy stole (dla max 9 graczy)
// Rozmieszczone zgodnie ze wskazówkami zegara, zaczynając od dołu (gracz lokalny)
// Indeks 0 = dół (zawsze gracz lokalny), potem w lewo i zgodnie z zegarem
const ALL_SEAT_POSITIONS: { x: number; y: number }[] = [
  { x: 960, y: 920 },     // 0: Dół środek (gracz lokalny)
  { x: 350, y: 780 },     // 1: Lewy dół
  { x: 150, y: 540 },     // 2: Lewy środek
  { x: 350, y: 300 },     // 3: Lewy góra
  { x: 700, y: 160 },     // 4: Góra lewa
  { x: 1220, y: 160 },    // 5: Góra prawa
  { x: 1570, y: 300 },    // 6: Prawy góra
  { x: 1770, y: 540 },    // 7: Prawy środek
  { x: 1570, y: 780 },    // 8: Prawy dół
];

// Pozycje zakładów dla każdego miejsca - bliżej środka stołu
const ALL_BET_POSITIONS: { x: number; y: number }[] = [
  { x: 960, y: 650 },     // 0: Dół - ponad kartami gracza lokalnego
  { x: 500, y: 680 },     // 1: Lewy dół
  { x: 380, y: 540 },     // 2: Lewy środek
  { x: 500, y: 400 },     // 3: Lewy góra
  { x: 750, y: 320 },     // 4: Góra lewa
  { x: 1170, y: 320 },    // 5: Góra prawa
  { x: 1420, y: 400 },    // 6: Prawy góra
  { x: 1540, y: 540 },    // 7: Prawy środek
  { x: 1420, y: 680 },    // 8: Prawy dół
];

// Mapowanie pozycji wizualnych dla różnych ilości miejsc przy stole
// Klucz = maxPlayers, wartość = indeksy z ALL_SEAT_POSITIONS do użycia
// Rozmieszczenie symetryczne wokół stołu
const SEAT_LAYOUTS: { [key: number]: number[] } = {
  2: [0, 4],                          // Naprzeciwko siebie (dół, góra-lewa jako środek)
  3: [0, 3, 6],                       // Trójkąt (dół, lewy-góra, prawy-góra)
  4: [0, 2, 5, 7],                    // Czworokąt (dół, lewy-środek, góra-prawa, prawy-środek)
  5: [0, 1, 4, 5, 8],                 // Pentagon (dół, lewy-dół, góra-lewa, góra-prawa, prawy-dół)
  6: [0, 1, 3, 5, 6, 8],              // Sześciokąt symetryczny
  7: [0, 1, 3, 4, 5, 6, 8],           // Siedmiokąt
  8: [0, 1, 2, 4, 5, 6, 7, 8],        // Ośmiokąt - pomija lewy-góra (3) dla symetrii
  9: [0, 1, 2, 3, 4, 5, 6, 7, 8],     // Wszystkie miejsca
};

// Mapowanie kart - wartości i kolory
const CARD_VALUES: { [key: string]: string } = {
  'A': 'A', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7',
  '8': '8', '9': '9', 'T': '10', 'J': 'W', 'Q': 'D', 'K': 'K',
  '10': '10',
};

const SUIT_EMOJI: { [key: string]: string } = {
  's': '♠', 'h': '♥', 'd': '♦', 'c': '♣',
  'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣',
  'spades': '♠', 'hearts': '♥', 'diamonds': '♦', 'clubs': '♣',
};

const SUIT_COLORS: { [key: string]: number } = {
  '♠': 0x0d1530, '♣': 0x0d1530,  // Czarne
  '♥': 0xcc0000, '♦': 0xcc0000,  // Czerwone
};

const QUICK_EMOJI_SET = ['😂', '❤️', '👍', '🙏', '😮', '😢', '😡', '🎉'];
const EMOJI_FONT_STACK = 'Noto Color Emoji, Apple Color Emoji, Segoe UI Emoji, sans-serif';
const EMOJI_WHEEL_RADIUS = 120;
const EMOJI_WHEEL_ITEM_RADIUS = 82;
const EMOJI_WHEEL_MARGIN = 30;
const EMOJI_TOGGLE_RADIUS = 26;

const AVATAR_SCALE = 0.75;
const AVATAR_DIAMETER = 200 * AVATAR_SCALE;
const AVATAR_RADIUS = AVATAR_DIAMETER / 2;
const AVATAR_CENTER_Y = -15;
const AVATAR_RING_PADDING = 4;
const AVATAR_NAME_OFFSET = 12;
const AVATAR_CHIPS_OFFSET = 32;
const AVATAR_TIMER_OFFSET = 57;
const AVATAR_CARD_GAP = 2;
const LOCAL_CARD_TOP_OFFSET = 92;
const CHIPS_BG_COLOR = 0x000000;
const CHIPS_BG_ALPHA = 0.6;
const CHIPS_BG_PADDING_X = 12;
const CHIPS_BG_PADDING_Y = 6;
const CHIPS_BG_RADIUS = 8;

interface CardDisplay {
  container: Phaser.GameObjects.Container;
  background: Phaser.GameObjects.Rectangle;
  valueText: Phaser.GameObjects.Text;
  suitText: Phaser.GameObjects.Text;
  backImage: Phaser.GameObjects.Image | null;
  faceImage: Phaser.GameObjects.Image | null;
  glowUnderlay: Phaser.GameObjects.Rectangle;
  targetWidth: number;
  targetHeight: number;

  // Cache last rendered state to avoid redundant texture/visibility changes
  // that can cause visible flicker during polling/turn updates.
  lastRenderMode?: 'hidden' | 'back' | 'face' | 'text';
  lastRenderKey?: string;
  lastRenderTextureKey?: string | null;
  lastRenderVisible?: boolean;
}

interface CardView {
  card: string;
  faceDown: boolean;
}

interface PlayerSeatUI {
  container: Phaser.GameObjects.Container;
  background: Phaser.GameObjects.Rectangle;
  avatar: Phaser.GameObjects.Image;
  nameText: Phaser.GameObjects.Text;
  chipsText: Phaser.GameObjects.Text;
  chipsBg: Phaser.GameObjects.Graphics;
  chipsIcon: Phaser.GameObjects.Arc;
  statusText: Phaser.GameObjects.Text;
  dealerChip: Phaser.GameObjects.Container;
  betContainer: Phaser.GameObjects.Container;
  betText: Phaser.GameObjects.Text;
  betChips: Phaser.GameObjects.Arc[];
  cards: CardDisplay[];
  highlightRing: Phaser.GameObjects.Arc;
  timerBar: Phaser.GameObjects.Rectangle;
  timerBarBg: Phaser.GameObjects.Rectangle;
}

export default class GameScene extends Phaser.Scene {
  // Table state
  private tableState: PokerTableState | null = null;
  private loosePlayActive = false;
  private localPlayerId: string = '';
  private maxPlayers: number = 9;
  private currentLayout: number[] = [];
  
  // UI elements
  private playerSeats: PlayerSeatUI[] = [];
  private tableBg!: Phaser.GameObjects.Image;
  private potText!: Phaser.GameObjects.Text;
  private potContainer!: Phaser.GameObjects.Container;
  private potPanelHeight: number = 0;
  private phaseText!: Phaser.GameObjects.Text;
  private communityCardsContainer!: Phaser.GameObjects.Container;
  private communityCards: CardDisplay[] = [];
  private pendingAvatarLoads = new Map<string, string>();
  
  // Current bet display (top right)
  private currentBetContainer!: Phaser.GameObjects.Container;
  private currentBetText!: Phaser.GameObjects.Text;
  
  // Action buttons
  private actionButtonsContainer!: Phaser.GameObjects.Container;
  private foldBtn!: Phaser.GameObjects.Image;
  private checkBtn!: Phaser.GameObjects.Image;
  private callBtn!: Phaser.GameObjects.Image;
  private raiseBtn!: Phaser.GameObjects.Image;
  private allInBtn!: Phaser.GameObjects.Image;
  // Button text references for dynamic updates
  private checkBtnText!: Phaser.GameObjects.Text;
  private callBtnText!: Phaser.GameObjects.Text;

  // Pre-game start vote overlay (shown in waiting phase)
  private startOverlay!: Phaser.GameObjects.Container;
  private startOverlayTitleText!: Phaser.GameObjects.Text;
  private startOverlayVotesText!: Phaser.GameObjects.Text;
  private startButton!: Phaser.GameObjects.Image;
  private startButtonText!: Phaser.GameObjects.Text;
  private startRequestInProgress: boolean = false;
  private minPlayersToStart: number = 2;
  private tableNeedsFreshStart: boolean = true;
  private forcedFreshStart: boolean = false;
  private wasBelowMinPlayers: boolean = true;
  
  // Raise slider
  private raiseSliderContainer!: Phaser.GameObjects.Container;
  private raiseSlider!: Phaser.GameObjects.Rectangle;
  private raiseSliderTrack!: Phaser.GameObjects.Rectangle;
  private raiseSliderHandle!: Phaser.GameObjects.Arc;
  private raiseAmountText!: Phaser.GameObjects.Text;
  private raiseDecreaseBtn!: Phaser.GameObjects.Rectangle;
  private raiseIncreaseBtn!: Phaser.GameObjects.Rectangle;
  private raiseInput!: Phaser.GameObjects.DOMElement;
  private raiseMinBtn!: Phaser.GameObjects.Rectangle;
  private raiseMaxBtn!: Phaser.GameObjects.Rectangle;
  private currentRaiseAmount: number = 0;
  private minRaise: number = 0;
  private maxRaise: number = 0;
  private raiseStep: number = 1;
  
  // Menu button
  private menuBtn!: Phaser.GameObjects.Image;
  private settingsButton!: Phaser.GameObjects.Image;
  private settingsPanelGroup!: Phaser.GameObjects.Container;
  private sfxSlider?: VolumeSlider;
  private musicSlider?: VolumeSlider;
  private audioSettings: AudioSettings = loadAudioSettings();
  private unsubscribeAudioSettings?: () => void;
  private deckSettings: DeckSettings = loadDeckSettings();
  private cardFaceMap: Record<string, string> = CARD_FACE_MAPS.classic;
  private deckButtons?: { classic: Phaser.GameObjects.Image; alternative: Phaser.GameObjects.Image };
  private deckButtonLabels?: { classic: Phaser.GameObjects.Text; alternative: Phaser.GameObjects.Text };
  private tableButtons?: { green: Phaser.GameObjects.Image; blue: Phaser.GameObjects.Image; red: Phaser.GameObjects.Image };
  private tableButtonLabels?: { green: Phaser.GameObjects.Text; blue: Phaser.GameObjects.Text; red: Phaser.GameObjects.Text };
  
  // PC Balance display
  private pcBalanceContainer!: Phaser.GameObjects.Container;
  private pcBalanceText!: Phaser.GameObjects.Text;
  
  // Leave confirmation popup
  private leavePopupGroup!: Phaser.GameObjects.Container;

  // Busted options popup
  private bustedOptionsPopupGroup!: Phaser.GameObjects.Container;
  
  // Buy-in popup
  private buyinPopupGroup!: Phaser.GameObjects.Container;
  private buyinSliderHandle!: Phaser.GameObjects.Arc;
  private buyinAmountText!: Phaser.GameObjects.Text;
  private buyinSubtitleText!: Phaser.GameObjects.Text;
  private buyinSliderFill!: Phaser.GameObjects.Rectangle;
  private buyinMinLabel!: Phaser.GameObjects.Text;
  private buyinMaxLabel!: Phaser.GameObjects.Text;
  private currentBuyinAmount: number = 0;
  private minBuyin: number = 0;
  private maxBuyin: number = 0;
  private buyinPopupShown: boolean = false;
  private hasSatDown: boolean = false;
  private manualBuyinOnly: boolean = false;
  
  // Status text
  private statusText!: Phaser.GameObjects.Text;

  // Emoji quick reactions
  private emojiToggleContainer!: Phaser.GameObjects.Container;
  private emojiToggleBg!: Phaser.GameObjects.Arc;
  private emojiWheelContainer!: Phaser.GameObjects.Container;
  private emojiWheelOpen: boolean = false;
  private lastEmojiSent: { emoji: string; at: number } | null = null;
  
  // Timer
  private turnTimer: Phaser.Time.TimerEvent | null = null;
  private currentTimerSeat: number = -1;
  private lastActivePlayerSeat: number = -1;
  
  // Polling timer for game state updates
  private pollingTimer: Phaser.Time.TimerEvent | null = null;
  
  // Action cooldown to prevent spam
  private actionInProgress: boolean = false;
  
  // Sitting state
  private isSeated: boolean = false;
  private isSittingOut: boolean = false;
  private pendingSitIn: boolean = false;
  private forceSitOutDueToStack: boolean = false;
  private minimumStackToReturn: number = 0;
  private forcedSitoutPrompt?: Phaser.GameObjects.Container;
  
  // Showdown revealed cards - keep them visible until next hand
  private revealedCards: Map<string, string[]> = new Map();
  private lastHandId: string = '';
  private lastHandNumber: number | null = null;
  private lastCommunityCards: string[] = [];
  private playerCardCounts: Map<string, number> = new Map();
  private animationsReady: boolean = false;
  private deckPosition = { x: 960, y: 540 };
  private availableCardBackKeys: string[] = [];
  private cardBackKey: string = '';

  // SFX helpers/state
  private lastBetByUserId: Map<string, number> = new Map();
  private lastFoldStateByUserId: Map<string, boolean> = new Map();
  private lastAllInStateByUserId: Map<string, boolean> = new Map();
  private lastSfxAtByKey: Map<string, number> = new Map();

  // Cache for highlighting the best hand locally
  private cachedBestHandCards: string[] = [];
  private cachedBestHandBoardKey: string = '';
  private lastBestHandFetchBoardKey: string = '';
  private bestHandFetchInProgress: boolean = false;

  // Showdown winner queue (supports side pots): displayed one-by-one via Enter
  private showdownWinnersQueue: Array<{ winnerName: string; amount: number; handDescription: string }> = [];
  private showdownWinnersOverlay: Phaser.GameObjects.Container | null = null;
  private winnerMessageContainer: Phaser.GameObjects.Container | null = null;
  private winnerStatusClearTimer: Phaser.Time.TimerEvent | null = null;
  private winnerStatusActive: boolean = false;
  private bustedMessageQueue: string[] = [];
  private bustedMessageText: Phaser.GameObjects.Text | null = null;
  private lastShowdownAtMs: number | null = null;
  
  // Track if user has manually adjusted raise slider
  private raiseSliderTouched: boolean = false;

  // Seat mapping can be intermittent in polling responses (seatNumber/position missing for a tick),
  // which would reshuffle visual positions and cause card containers to be reset/re-shown (flicker).
  private lastKnownServerSeatByUserId: Map<string, number> = new Map();
  private lastKnownLocalServerSeat: number | null = null;

  // Coalesce frequent state updates (polling + events + best-hand fetch) into a single UI update.
  private queuedTableState: PokerTableState | null = null;
  private tableStateUpdateScheduled: boolean = false;
  private lastTableStateAppliedAt: number = 0;

  // Avoid overlapping refresh requests (multiple event handlers can trigger refresh back-to-back).
  private refreshInFlight: boolean = false;
  private refreshQueued: boolean = false;

  // Auto fast-forward (runout) when no further decisions are possible
  private autoFastForwardPending: boolean = false;
  private autoFastForwardLastAt: number = 0;
  private autoFastForwardHandNumber: number | null = null;
  private autoFastForwardChecksThisHand: number = 0;

  constructor() {
    super({ key: 'GameScene' });
  }

  private parseHandNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private getStableServerSeat(player: PlayerState, fallbackIndex: number, maxPlayers: number): number {
    const seatNumber = (player as any).seatNumber;
    const position = (player as any).position;
    const candidate = typeof seatNumber === 'number' && Number.isFinite(seatNumber)
      ? seatNumber
      : (typeof position === 'number' && Number.isFinite(position) ? position : null);

    if (candidate !== null) {
      this.lastKnownServerSeatByUserId.set(player.userId, candidate);
      if (player.userId === this.localPlayerId) {
        this.lastKnownLocalServerSeat = candidate;
      }
      return candidate;
    }

    const cached = this.lastKnownServerSeatByUserId.get(player.userId);
    if (typeof cached === 'number' && Number.isFinite(cached)) {
      return cached;
    }

    // Last resort fallback: keep deterministic placement rather than defaulting to 0.
    const safeMax = Math.max(1, maxPlayers || 1);
    return ((fallbackIndex % safeMax) + safeMax) % safeMax;
  }

  private queueTableStateUpdate(state: PokerTableState): void {
    // Preserve myCards from previous state if missing in new state (prevents flickering on partial updates)
    if (this.tableState && this.tableState.myCards && this.tableState.myCards.length > 0 && (!state.myCards || state.myCards.length === 0)) {
      const prevHand = this.parseHandNumber((this.tableState as any).currentHandNumber);
      const nextHand = this.parseHandNumber((state as any).currentHandNumber);
      
      // Only preserve cards if we are in the same hand
      if (prevHand !== null && nextHand !== null && prevHand === nextHand) {
        state.myCards = this.tableState.myCards;
      }
    }

    this.queuedTableState = state;
    if (this.tableStateUpdateScheduled) return;
    this.tableStateUpdateScheduled = true;

    // Schedule for next tick to collapse multiple sources into one UI update.
    this.time.delayedCall(0, () => {
      this.tableStateUpdateScheduled = false;
      const latest = this.queuedTableState;
      this.queuedTableState = null;
      if (!latest) return;
      this.tableState = latest;
      this.lastTableStateAppliedAt = Date.now();
      this.updateTableUI(latest);
    });
  }

  private clearHandUiCaches(reason: string): void {
    console.log('[GameScene] Clearing hand UI caches:', reason);
    this.revealedCards.clear();
    this.playerCardCounts.clear();
    this.lastCommunityCards = [];
    this.cachedBestHandCards = [];
    this.cachedBestHandBoardKey = '';
    this.lastBestHandFetchBoardKey = '';
    this.clearWinnerAnnouncements();
    this.clearAllCardGlows();
  }

  /**
   * Translate poker hand names from English to Polish
   */
  private translateHandName(handName: string): string {
    const translations: { [key: string]: string } = {
      'Royal Flush': 'Poker Królewski',
      'Straight Flush': 'Poker',
      'Four of a Kind': 'Kareta',
      'Full House': 'Full',
      'Flush': 'Kolor',
      'Straight': 'Strit',
      'Three of a Kind': 'Trójka',
      'Two Pair': 'Dwie Pary',
      'Pair': 'Para',
      'High Card': 'Wysoka Karta',
    };
    
    // Try exact match first
    if (translations[handName]) {
      return translations[handName];
    }
    
    // Try partial match (for descriptions like "Pair, Kings")
    for (const [eng, pol] of Object.entries(translations)) {
      if (handName.includes(eng)) {
        return handName.replace(eng, pol);
      }
    }
    
    return handName; // Return original if no translation found
  }

  init(data: { tableState?: PokerTableState; playerId?: string; maxPlayers?: number; tableId?: string }): void {
    this.tableState = data.tableState || null;
    this.localPlayerId = data.playerId || pokerClient.userId;
    this.maxPlayers = data.maxPlayers || data.tableState?.maxPlayers || 9;
    this.forceSitOutDueToStack = false;
    this.minimumStackToReturn = 0;
    
    // Get layout for this table size
    this.currentLayout = SEAT_LAYOUTS[this.maxPlayers] || SEAT_LAYOUTS[9];
    
    // Store tableId if provided
    if (data.tableId && !pokerClient.tableId) {
      console.log('[GameScene] Setting tableId from init data:', data.tableId);
    }
  }

  preload(): void {
    // Load reverse (card back) textures so we can pick one per table
    Object.values(CARD_BACK_ASSETS_BY_DECK).flat().forEach((asset) => {
      if (!this.textures.exists(asset.key)) {
        this.load.image(asset.key, asset.url);
      }
    });

    // Load all card face textures (fronts) for both decks
    Object.values(CARD_FACE_ASSETS_BY_DECK).flat().forEach((asset) => {
      if (!this.textures.exists(asset.key)) {
        this.load.image(asset.key, asset.url);
      }
    });

    // Load SFX
    AUDIO_ASSETS.forEach((asset) => {
      if (!this.cache.audio.exists(asset.key)) {
        this.load.audio(asset.key, asset.url);
      }
    });

    // Assets should already be loaded, but ensure they exist
    if (!this.textures.exists('table_bg')) {
      this.load.image('table_bg', 'assets/table_bg_asset.webp');
    }
    if (!this.textures.exists('table_bg_blue')) {
      this.load.image('table_bg_blue', 'assets/table_blue_bg_asset.webp');
    }
    if (!this.textures.exists('table_bg_red')) {
      this.load.image('table_bg_red', 'assets/table_red_bg_asset.webp');
    }
    if (!this.textures.exists('button1')) {
      this.load.image('button1', 'assets/button_1.webp');
    }
    if (!this.textures.exists('button2')) {
      this.load.image('button2', 'assets/button_2.webp');
    }
    if (!this.textures.exists('button_small')) {
      this.load.image('button_small', 'assets/button_1_small.webp');
    }
    if (!this.textures.exists('settings_button')) {
      this.load.image('settings_button', 'assets/settingsButton_asset.webp');
    }
    if (!this.textures.exists('pc_acc')) {
      this.load.image('pc_acc', 'assets/pc_acc_asset_new.webp');
    }

    // POT / Do wyrównania panels
    if (!this.textures.exists('pot_panel')) {
      this.load.image('pot_panel', 'assets/POT_asset.webp');
    }
    if (!this.textures.exists('equal_panel')) {
      this.load.image('equal_panel', 'assets/equal_asset.webp');
    }
    // Load avatars 1-9
    AVATAR_PRESET_KEYS.forEach((key) => {
      if (!this.textures.exists(key)) {
        this.load.image(key, `assets/Avatars/${key.replace('avatar', '')}.webp`);
      }
    });
  }

  create(): void {
    this.audioSettings = loadAudioSettings();
    this.deckSettings = loadDeckSettings();
    const deckBackKeys = CARD_BACK_KEYS_BY_DECK[this.deckSettings.selectedDeck] || [];
    this.availableCardBackKeys = deckBackKeys.length > 0 ? deckBackKeys : CARD_BACK_KEYS_BY_DECK.classic;
    this.cardBackKey = this.pickRandomCardBack();
    this.cardFaceMap = CARD_FACE_MAPS[this.deckSettings.selectedDeck] || CARD_FACE_MAPS.classic;

    // Reset all arrays and state for clean scene restart
    this.playerSeats = [];
    this.communityCards = [];
    this.turnTimer = null;
    this.pollingTimer = null;
    this.currentTimerSeat = -1;
    this.lastActivePlayerSeat = -1;
    this.isSeated = false;
    this.isSittingOut = false;
    this.pendingSitIn = false;
    this.actionInProgress = false;
    this.buyinPopupShown = false;
    this.hasSatDown = false;
    this.manualBuyinOnly = false;
    this.playerCardCounts.clear();
    this.lastBetByUserId.clear();
    this.lastFoldStateByUserId.clear();
    this.lastAllInStateByUserId.clear();
    this.lastSfxAtByKey.clear();
    this.lastCommunityCards = [];
    this.lastHandNumber = null;
    this.animationsReady = false;
    this.pendingAvatarLoads.clear();
    this.emojiWheelOpen = false;
    this.lastEmojiSent = null;

    // Reset showdown winner queue/overlay
    this.showdownWinnersQueue = [];
    if (this.showdownWinnersOverlay) {
      this.showdownWinnersOverlay.destroy(true);
      this.showdownWinnersOverlay = null;
    }
    if (this.winnerMessageContainer) {
      this.winnerMessageContainer.destroy(true);
      this.winnerMessageContainer = null;
    }
    if (this.winnerStatusClearTimer) {
      this.winnerStatusClearTimer.destroy();
      this.winnerStatusClearTimer = null;
    }
    this.winnerStatusActive = false;

    // Reset start overlay if scene restarts
    this.startRequestInProgress = false;
    if (this.startOverlay) {
      this.startOverlay.destroy(true);
    }
    
    this.cameras.main.setBackgroundColor('#050814');
    
    // Table background - full screen 1920x1080
    this.tableBg = this.add.image(960, 540, this.getTableBgKey());
    this.tableBg.setDisplaySize(1920, 1080);

    // Create community cards area
    this.createCommunityCardsArea();
    
    // Create pot display
    this.createPotDisplay();
    
    // Create current bet display (top right)
    this.createCurrentBetDisplay();
    
    // Create player seats
    this.createPlayerSeats();
    
    // Create action buttons
    this.createActionButtons();
    
    // Create raise slider
    this.createRaiseSlider();
    
    // Create menu button
    this.createMenuButton();

    // Create settings button + panel
    this.createSettingsButton();
    this.createSettingsPanel();
    this.layoutTopButtons();

    // Create emoji quick reactions UI
    this.createEmojiWheel();
    
    // Create PC balance display
    this.createPCBalanceDisplay();
    
    // Create leave confirmation popup
    this.createLeavePopup();
    
    // Create buy-in popup
    this.createBuyinPopup();

    // Create busted options popup
    this.createBustedOptionsPopup();

    // Create start vote overlay (shown when table is waiting for first hand)
    this.createStartOverlay();
    
    // Status text
    this.statusText = this.add.text(960, 50, '', {
      fontSize: '20px',
      color: '#ffd93d',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Setup event listeners
    this.setupClientEvents();

    // Cycle showdown winners with Enter
    this.input.keyboard?.off('keydown-ENTER', this.onEnterKey, this);
    this.input.keyboard?.on('keydown-ENTER', this.onEnterKey, this);
    
    // Register shutdown handler
    this.events.on('shutdown', this.shutdown, this);
    this.unsubscribeAudioSettings = onAudioSettingsChanged((settings) => {
      this.audioSettings = settings;
      this.sfxSlider?.updateFromValue(settings.sfxVolume);
      this.musicSlider?.updateFromValue(settings.musicVolume);
    });
    
    // Load initial state if available
    if (this.tableState) {
      this.updateTableUI(this.tableState);
    } else {
      this.refreshTableState();
    }
    
    // Hide action buttons initially
    this.setActionButtonsVisible(false);
    this.raiseSliderContainer.setVisible(false);
  }

  private createStartOverlay(): void {
    this.startOverlay = this.add.container(960, 690);
    this.startOverlay.setDepth(150);

    const bg = this.add.rectangle(0, 0, 900, 190, 0x000000, 0.78);
    bg.setStrokeStyle(2, 0xd4af37);

    this.startOverlayTitleText = this.add.text(0, -60, 'ROZPOCZNIJ PIERWSZĄ PARTIĘ', {
      fontSize: '34px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);

    this.startButton = this.add.image(0, 5, 'button_small').setScale(0.62);
    this.startButton.setInteractive({ useHandCursor: true });

    this.startButtonText = this.add.text(0, 5, 'START', {
      fontSize: '26px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);

    this.startOverlayVotesText = this.add.text(0, 70, '', {
      fontSize: '18px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    this.startButton.on('pointerover', () => this.startButton.setTint(0xcccccc));
    this.startButton.on('pointerout', () => this.startButton.clearTint());
    this.startButton.on('pointerdown', async () => {
      if (this.startRequestInProgress) return;
      if (!this.tableState) return;
      const phase = this.tableState.currentPhase || this.tableState.phase || 'waiting';
      if (phase !== 'waiting') return;

      this.startRequestInProgress = true;
      this.startButton.disableInteractive();
      this.startButton.setAlpha(0.7);
      this.startButtonText.setText('...');

      try {
        const res = await pokerClient.requestStart();
        if (!res.success) {
          this.statusText.setText(`Błąd startu: ${res.error || 'nieznany błąd'}`).setColor('#ff6b6b');
          this.time.delayedCall(2500, () => {
            if (this.statusText.text.startsWith('Błąd startu:')) {
              this.statusText.setText('');
            }
          });
        }
      } catch (e) {
        this.statusText.setText('Błąd startu gry').setColor('#ff6b6b');
        this.time.delayedCall(2500, () => {
          if (this.statusText.text === 'Błąd startu gry') {
            this.statusText.setText('');
          }
        });
      } finally {
        this.startRequestInProgress = false;
        this.startButton.setAlpha(1);
        this.startButton.setInteractive({ useHandCursor: true });
        // Force refresh so vote counts update immediately on click
        this.refreshTableState();
      }
    });

    this.startOverlay.add([
      bg,
      this.startOverlayTitleText,
      this.startButton,
      this.startButtonText,
      this.startOverlayVotesText,
    ]);

    this.startOverlay.setVisible(false);
  }

  private updateFreshStartState(state: PokerTableState): void {
    const phase = state.currentPhase || state.phase || 'waiting';
    const seatedPlayers = (state.players || []).filter((p: any) => !!p) as PlayerState[];
    const playerCount = seatedPlayers.length;
    const belowMinPlayers = playerCount < this.minPlayersToStart;

    if (belowMinPlayers) {
      if (!this.wasBelowMinPlayers) {
        this.tableNeedsFreshStart = true;
        this.forcedFreshStart = true;
        this.startRequestInProgress = false;
      }
      this.wasBelowMinPlayers = true;
    } else {
      this.wasBelowMinPlayers = false;
    }

    if (!this.forcedFreshStart) {
      if (typeof state.waitingForFirstHand === 'boolean') {
        this.tableNeedsFreshStart = state.waitingForFirstHand;
      } else if (typeof state.hasStarted === 'boolean') {
        this.tableNeedsFreshStart = !state.hasStarted;
      }
    }

    if (phase !== 'waiting') {
      this.tableNeedsFreshStart = false;
      this.forcedFreshStart = false;
    }
  }

  private updateStartOverlay(state: PokerTableState): void {
    if (!this.startOverlay) return;

    const phase = state.currentPhase || state.phase || 'waiting';
    const seatedPlayers = (state.players || []).filter((p: any) => !!p) as PlayerState[];
    const playerCount = seatedPlayers.length;
    const localPlayer = seatedPlayers.find(p => p.userId === this.localPlayerId);

    // Show for everyone at the table when the game is waiting to start.
    const buyinVisible = !!this.buyinPopupGroup && this.buyinPopupGroup.visible;
    const show =
      phase === 'waiting' &&
      playerCount >= this.minPlayersToStart &&
      this.tableNeedsFreshStart &&
      !buyinVisible;

    this.startOverlay.setVisible(show);
    if (!show) return;

    const votes = state.startVotes || [];
    const votedCount = votes.length;
    const required =
      typeof state.startVotesRequired === 'number'
        ? state.startVotesRequired
        : Math.max(1, Math.ceil(playerCount * 0.5));

    const hasVoted = votes.includes(this.localPlayerId);
    this.startOverlayVotesText.setText(`Głosy startu: ${votedCount}/${required} (min 50%)`);

    if (this.startRequestInProgress) {
      this.startButton.disableInteractive();
      this.startButton.setAlpha(0.7);
      this.startButtonText.setText('...');
      return;
    }

    if (hasVoted) {
      this.startButton.disableInteractive();
      this.startButton.setAlpha(0.6);
      this.startButtonText.setText('ZAGŁOSOWANO');
    } else {
      this.startButton.setInteractive({ useHandCursor: true });
      this.startButton.setAlpha(1);
      this.startButtonText.setText('START');
    }
  }

  private createCommunityCardsArea(): void {
    // Container at origin, cards positioned absolutely
    this.communityCardsContainer = this.add.container(0, 0);
    
    // Card dimensions matching background: 90x130px, 22px gaps
    // Starting point: 685x426px (top-left of first card)
    const cardWidth = 90;
    const cardHeight = 130;
    const spacing = 22;
    const startX = 685;
    const startY = 426;
    
    for (let i = 0; i < 5; i++) {
      // Calculate center position for each card
      const centerX = startX + (cardWidth / 2) + i * (cardWidth + spacing);
      const centerY = startY + (cardHeight / 2);
      
      const card = this.createCardDisplay(centerX, centerY, cardWidth, cardHeight);
      this.communityCardsContainer.add(card.container);
      this.communityCards.push(card);
    }
  }

  /**
   * Tworzy wyświetlacz karty z wartością i kolorem
   */
  private createCardDisplay(x: number, y: number, width: number, height: number): CardDisplay {
    const container = this.add.container(x, y);
    const fallbackBackKey = this.cardBackKey
      || this.availableCardBackKeys[0]
      || CARD_BACK_KEYS_BY_DECK.classic[0]
      || '';
    
    // Tło karty
    const background = this.add.rectangle(0, 0, width, height, 0xf5f5dc, 0.95);
    background.setStrokeStyle(2, 0x333333);
    container.add(background);

    // Grafika awersu (prawdziwa karta)
    const faceImage = this.add.image(0, 0, fallbackBackKey);
    this.fitCardImage(faceImage, width, height);
    faceImage.setVisible(false);
    container.add(faceImage);

    // Grafika rewersu (domyślnie niewidoczna, pokazywana przy zakrytej karcie)
    const backImage = this.add.image(0, 0, fallbackBackKey);
    this.fitCardImage(backImage, width, height);
    backImage.setVisible(false);
    container.add(backImage);
    
    // Wartość karty (A, K, D, W, 10, 9...)
    const valueText = this.add.text(-width/2 + 8, -height/2 + 5, '', {
      fontSize: width > 60 ? '24px' : '16px',
      color: '#0d1530',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0, 0);
    container.add(valueText);
    
    // Kolor karty (emoji)
    const suitText = this.add.text(0, height/6, '', {
      fontSize: width > 60 ? '36px' : '24px',
      color: '#cc0000',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    container.add(suitText);

    // Delikatny glow - dodany na wierzchu, aby obwódka była widoczna nad grafiką karty
    const glowUnderlay = this.add.rectangle(0, 0, width - 6, height - 6, 0x000000, 0);
    glowUnderlay.setStrokeStyle(3, 0xffd93d, 0.8);
    this.resizeGlowUnderlay({ glowUnderlay, targetWidth: width, targetHeight: height });
    glowUnderlay.setVisible(false);
    container.add(glowUnderlay);
    
    // Domyślnie ukryta
    container.setVisible(false);
    
    return {
      container,
      background,
      valueText,
      suitText,
      backImage,
      faceImage,
      glowUnderlay,
      targetWidth: width,
      targetHeight: height,

      lastRenderMode: 'hidden',
      lastRenderKey: '',
      lastRenderTextureKey: null,
      lastRenderVisible: false,
    };
  }

  private resizeCardDisplay(card: CardDisplay, width: number, height: number): void {
    if (card.targetWidth === width && card.targetHeight === height) return;

    card.targetWidth = width;
    card.targetHeight = height;

    card.background.setSize(width, height);
    card.background.setDisplaySize(width, height);

    const valueFontSize = width > 60 ? '24px' : '16px';
    const suitFontSize = width > 60 ? '36px' : '24px';
    card.valueText.setFontSize(valueFontSize);
    card.valueText.setPosition(-width / 2 + 8, -height / 2 + 5);
    card.suitText.setFontSize(suitFontSize);
    card.suitText.setPosition(0, height / 6);

    this.resizeGlowUnderlay(card);

    if (card.faceImage) {
      this.fitCardImage(card.faceImage, width, height);
    }
    if (card.backImage) {
      this.fitCardImage(card.backImage, width, height);
    }
  }

  private resizeGlowUnderlay(card: Pick<CardDisplay, 'glowUnderlay' | 'targetWidth' | 'targetHeight'>): void {
    const strokeThickness = 3;
    const isNeonDeck = this.deckSettings.selectedDeck === 'alternative';
    const glowWidth = isNeonDeck ? card.targetWidth + strokeThickness : card.targetWidth - 6;
    const glowHeight = isNeonDeck ? card.targetHeight + strokeThickness : card.targetHeight - 6;
    card.glowUnderlay.setSize(glowWidth, glowHeight);
    card.glowUnderlay.setDisplaySize(glowWidth, glowHeight);
  }

  private refreshGlowUnderlays(): void {
    this.communityCards.forEach(card => this.resizeGlowUnderlay(card));
    this.playerSeats.forEach(seat => seat.cards.forEach(card => this.resizeGlowUnderlay(card)));
  }

  private layoutSeatCards(
    seat: PlayerSeatUI,
    cardWidth: number,
    cardHeight: number,
    cardY: number,
    animateGrow: boolean,
  ): void {
    const spacing = 5;
    const sizeChanged = seat.cards.some(card => card.targetWidth !== cardWidth || card.targetHeight !== cardHeight);
    if (!sizeChanged) return;

    seat.cards.forEach((card, idx) => {
      const isGrowing = cardWidth > card.targetWidth && cardHeight > card.targetHeight;
      const scaleStart = isGrowing ? card.targetWidth / cardWidth : 1;
      const cardX = (idx - 0.5) * (cardWidth + spacing);
      card.container.setPosition(cardX, cardY);
      this.resizeCardDisplay(card, cardWidth, cardHeight);
      if (animateGrow && isGrowing) {
        card.container.setScale(scaleStart);
        this.tweens.add({
          targets: card.container,
          scale: 1,
          duration: 220,
          ease: 'Cubic.easeOut',
        });
      } else {
        card.container.setScale(1);
      }
    });
  }

  private shouldEnlargeOpponentCards(state: PokerTableState): boolean {
    const phase = state.currentPhase || state.phase || 'waiting';
    if (phase === 'showdown' || phase === 'hand_complete' || phase === 'ended') return true;
    return phase === 'waiting' && this.revealedCards.size > 0;
  }

  private fitCardImage(image: Phaser.GameObjects.Image, targetWidth: number, targetHeight: number): void {
    const texWidth = image.width || 1;
    const texHeight = image.height || 1;
    const scale = Math.min(targetWidth / texWidth, targetHeight / texHeight);
    image.setScale(scale);
  }

  private resetCardContainerTransform(container: Phaser.GameObjects.Container): void {
    container.setAlpha(1);
    container.setScale(1);
    container.setAngle(0);
  }

  private ensureCardContainerStable(container: Phaser.GameObjects.Container): void {
    // If a tween is currently animating transform properties, don't fight it.
    if (this.tweens.isTweening(container)) return;
    this.resetCardContainerTransform(container);
  }

  private normalizeCardCode(cardString: string | null): string | null {
    if (!cardString || cardString.length < 2) return null;
    const valuePart = cardString.slice(0, -1);
    const suitPart = cardString.slice(-1).toLowerCase();
    const normalizedValue = valuePart === 'T' ? '10' : valuePart.toLowerCase();
    return `${normalizedValue}${suitPart}`;
  }

  /**
   * Parsuje string karty (np. "As", "Kh", "Td") na wartość i kolor
   */
  private parseCard(cardString: string): { value: string; suit: string; color: number } | null {
    if (!cardString || cardString.length < 2) return null;
    
    // Format: wartość + kolor, np. "As" (As of spades), "Kh" (King of hearts)
    const valueChar = cardString.slice(0, -1);
    const suitChar = cardString.slice(-1);
    
    const value = CARD_VALUES[valueChar] || valueChar;
    const suit = SUIT_EMOJI[suitChar] || suitChar;
    const color = SUIT_COLORS[suit] || 0x0d1530;
    
    return { value, suit, color };
  }

  /**
   * Aktualizuje wyświetlacz karty
   */
  private updateCardDisplay(card: CardDisplay, cardString: string | null, faceDown: boolean = false): void {
    // Compute desired render state first; then apply only if it differs from current.
    // This reduces redundant setTexture/setVisible calls that can cause flicker.
    let desiredMode: 'hidden' | 'back' | 'face' | 'text' = 'hidden';
    let desiredKey = '';
    let desiredTextureKey: string | null = null;
    let desiredVisible = false;

    if (faceDown) {
      desiredMode = 'back';
      desiredVisible = true;
      desiredTextureKey = this.cardBackKey || card.backImage?.texture?.key || null;
      desiredKey = `back:${desiredTextureKey || ''}`;
    } else {
      const normalizedCode = this.normalizeCardCode(cardString);
      const faceKey = normalizedCode ? this.cardFaceMap[normalizedCode] : undefined;
      if (faceKey && card.faceImage && this.textures.exists(faceKey)) {
        desiredMode = 'face';
        desiredVisible = true;
        desiredTextureKey = faceKey;
        desiredKey = `face:${faceKey}`;
      } else {
        const parsed = cardString ? this.parseCard(cardString) : null;
        if (parsed) {
          desiredMode = 'text';
          desiredVisible = true;
          desiredKey = `text:${parsed.value}:${parsed.suit}:${parsed.color}`;
        } else {
          desiredMode = 'hidden';
          desiredVisible = false;
          desiredKey = 'hidden';
        }
      }
    }

    const lastMode = card.lastRenderMode ?? 'hidden';
    const lastKey = card.lastRenderKey ?? '';
    const lastTex = card.lastRenderTextureKey ?? null;
    const lastVisible = card.lastRenderVisible ?? false;
    let isSame =
      lastMode === desiredMode &&
      lastKey === desiredKey &&
      lastTex === desiredTextureKey &&
      lastVisible === desiredVisible;

    // If any other code path toggled visibility/texture directly, don't trust the cache.
    if (isSame) {
      if (card.container.visible !== desiredVisible) {
        isSame = false;
      } else if (desiredMode === 'back' && card.backImage) {
        if (!card.backImage.visible) isSame = false;
        if (desiredTextureKey && card.backImage.texture.key !== desiredTextureKey) isSame = false;
      } else if (desiredMode === 'face' && card.faceImage) {
        if (!card.faceImage.visible) isSame = false;
        if (desiredTextureKey && card.faceImage.texture.key !== desiredTextureKey) isSame = false;
      }
    }

    if (isSame) {
      // Keep transforms stable if nothing changed.
      if (desiredVisible) {
        this.ensureCardContainerStable(card.container);
      }
      return;
    }

    // Apply render state
    if (!desiredVisible) {
      // Hide only when transitioning to hidden
      this.tweens.killTweensOf(card.container);
      this.resetCardContainerTransform(card.container);
      card.container.setVisible(false);
      card.lastRenderMode = desiredMode;
      card.lastRenderKey = desiredKey;
      card.lastRenderTextureKey = desiredTextureKey;
      card.lastRenderVisible = desiredVisible;
      return;
    }

    this.ensureCardContainerStable(card.container);
    card.container.setVisible(true);

    if (desiredMode === 'back') {
      // Zakryta karta: pokaż rewers, ukryj wartości
      card.background.setVisible(false);
      if (card.faceImage) {
        card.faceImage.setVisible(false);
      }
      if (card.backImage) {
        if (desiredTextureKey && card.backImage.texture.key !== desiredTextureKey) {
          card.backImage.setTexture(desiredTextureKey);
        }
        this.fitCardImage(card.backImage, card.targetWidth, card.targetHeight);
        card.backImage.setVisible(true);
      }
      card.valueText.setVisible(false);
      card.suitText.setVisible(false);
    } else if (desiredMode === 'face') {
      card.background.setVisible(false);
      if (card.faceImage) {
        card.faceImage.setVisible(true);
        if (desiredTextureKey && card.faceImage.texture.key !== desiredTextureKey) {
          card.faceImage.setTexture(desiredTextureKey);
        }
        this.fitCardImage(card.faceImage, card.targetWidth, card.targetHeight);
      }
      if (card.backImage) {
        card.backImage.setVisible(false);
      }
      card.valueText.setVisible(false);
      card.suitText.setVisible(false);
    } else {
      // Text-based face
      const parsed = cardString ? this.parseCard(cardString) : null;
      if (!parsed) {
        // Defensive fallback
        this.tweens.killTweensOf(card.container);
        this.resetCardContainerTransform(card.container);
        card.container.setVisible(false);
        card.lastRenderMode = 'hidden';
        card.lastRenderKey = 'hidden';
        card.lastRenderTextureKey = null;
        card.lastRenderVisible = false;
        return;
      }

      card.background.setVisible(true);
      if (card.backImage) {
        card.backImage.setVisible(false);
      }
      if (card.faceImage) {
        card.faceImage.setVisible(false);
      }
      card.valueText.setVisible(true);
      card.suitText.setVisible(true);
      card.background.setFillStyle(0xf5f5dc, 0.95);
      card.background.setStrokeStyle(2, 0x333333);
      if (card.valueText.text !== parsed.value) {
        card.valueText.setText(parsed.value);
      }
      const valueColor = parsed.color === 0xcc0000 ? '#cc0000' : '#0d1530';
      card.valueText.setColor(valueColor);
      if (card.suitText.text !== parsed.suit) {
        card.suitText.setText(parsed.suit);
      }
      const suitColor = parsed.color === 0xcc0000 ? '#cc0000' : '#0d1530';
      card.suitText.setColor(suitColor);
    }

    // Update cache
    card.lastRenderMode = desiredMode;
    card.lastRenderKey = desiredKey;
    card.lastRenderTextureKey = desiredTextureKey;
    card.lastRenderVisible = desiredVisible;
  }

  private setCardGlow(card: CardDisplay, enabled: boolean): void {
    if (card.glowUnderlay) {
      card.glowUnderlay.setVisible(enabled);
    }
  }

  private clearAllCardGlows(): void {
    this.communityCards.forEach(card => this.setCardGlow(card, false));
    this.playerSeats.forEach(seat => seat.cards.forEach(card => this.setCardGlow(card, false)));
  }

  private getSfxKeysByPrefix(prefix: string): string[] {
    return AUDIO_ASSETS.filter(a => a.name.startsWith(prefix)).map(a => a.key);
  }

  private playSfx(keys: string[], options?: { volume?: number; rateLimitKey?: string; minIntervalMs?: number }): void {
    if (!keys || keys.length === 0) return;
    if (!this.sound || (this.sound as any).locked) return;

    const pickedKey = keys[Phaser.Math.Between(0, keys.length - 1)];
    if (!this.cache.audio.exists(pickedKey)) return;

    // Default: subtle, frequent SFX (realistic table feel)
    const baseVolume = options?.volume ?? 0.18;
    const volume = Phaser.Math.Clamp(baseVolume * this.audioSettings.sfxVolume, 0, 1);
    const rateLimitKey = options?.rateLimitKey || pickedKey;
    const minIntervalMs = options?.minIntervalMs ?? 35;
    const now = Date.now();
    const lastAt = this.lastSfxAtByKey.get(rateLimitKey) ?? 0;
    if (now - lastAt < minIntervalMs) return;
    this.lastSfxAtByKey.set(rateLimitKey, now);

    try {
      this.sound.play(pickedKey, { volume });
    } catch (e) {
      // Ignore audio errors (e.g., browser lock) to avoid breaking gameplay
    }
  }

  private playSfxByPrefixes(prefixes: string[], options?: { volume?: number; rateLimitKey?: string; minIntervalMs?: number }): void {
    const keys: string[] = [];
    prefixes.forEach((p) => keys.push(...this.getSfxKeysByPrefix(p)));
    this.playSfx(keys, options);
  }

  /**
   * Tworzy wyświetlacz aktualnego zakładu w prawym górnym rogu
   */
  private createCurrentBetDisplay(): void {
    // "Do wyrównania" panel under POT, centered above the board
    this.currentBetContainer = this.add.container(960, 0);
    this.currentBetContainer.setDepth(100);

    const maxPanelWidth = 200;
    const bgImage = this.add.image(0, 0, 'equal_panel').setOrigin(0.5);
    const bgScale = bgImage.width > 0 ? Math.min(1, maxPanelWidth / bgImage.width) : 1;
    bgImage.setScale(bgScale);

    const label = this.add.text(0, -13, 'DO WYRÓWNANIA', {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 1,
      align: 'center',
    }).setOrigin(0.5);

    // Kwota
    this.currentBetText = this.add.text(0, 7, '0', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 1,
      align: 'center',
    }).setOrigin(0.5);

    this.currentBetContainer.add([bgImage, label, this.currentBetText]);

    // Position 15px below POT panel
    const potY = this.potContainer?.y ?? 290;
    const potHeight = this.potPanelHeight || this.potContainer?.getBounds().height || 0;
    const equalHeight = bgImage.displayHeight || bgImage.height || 0;
    const y = potY + potHeight / 2 + 15 + equalHeight / 2;
    this.currentBetContainer.setPosition(960, y);
  }

  private createPotDisplay(): void {
    // POT panel slightly higher than before, centered above community cards
    this.potContainer = this.add.container(960, 290);
    this.potContainer.setDepth(100);

    const maxPanelWidth = 200;
    const bgImage = this.add.image(0, 0, 'pot_panel').setOrigin(0.5);
    const bgScale = bgImage.width > 0 ? Math.min(1, maxPanelWidth / bgImage.width) : 1;
    bgImage.setScale(bgScale);
    this.potPanelHeight = bgImage.displayHeight || 0;

    const label = this.add.text(0, -13, 'POT', {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 1,
      align: 'center',
    }).setOrigin(0.5);

    // Pot amount
    this.potText = this.add.text(0, 7, '0', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 1,
      align: 'center',
    }).setOrigin(0.5);

    this.potContainer.add([bgImage, label, this.potText]);
    
    // Phase indicator - below the cards
    this.phaseText = this.add.text(960, 580, '', {
      fontSize: '16px',
      color: '#6bcf7f',
      fontFamily: 'Rajdhani, Arial, sans-serif',
    }).setOrigin(0.5);
  }

  private createPlayerSeats(): void {
    // Create seats based on current layout (maxPlayers)
    for (let visualIndex = 0; visualIndex < this.currentLayout.length; visualIndex++) {
      const positionIndex = this.currentLayout[visualIndex];
      const seat = this.createPlayerSeat(positionIndex, visualIndex);
      this.playerSeats.push(seat);
    }
  }

  private createPlayerSeat(positionIndex: number, visualIndex: number): PlayerSeatUI {
    const pos = ALL_SEAT_POSITIONS[positionIndex];
    const betPos = ALL_BET_POSITIONS[positionIndex];
    
    const container = this.add.container(pos.x, pos.y);
    const avatarTopY = AVATAR_CENTER_Y - AVATAR_RADIUS;
    const avatarBottomY = AVATAR_CENTER_Y + AVATAR_RADIUS;
    
    // Highlight ring (for current player turn)
    const highlightRing = this.add.circle(0, AVATAR_CENTER_Y, AVATAR_RADIUS + AVATAR_RING_PADDING, 0xffd93d, 0);
    highlightRing.setStrokeStyle(6, 0xffd93d);
    highlightRing.setVisible(false);
    container.add(highlightRing);
    
    const timerY = avatarBottomY + AVATAR_TIMER_OFFSET;
    // Timer bar background
    const timerBarBg = this.add.rectangle(0, timerY, 120, 8, 0x333333);
    timerBarBg.setVisible(false);
    container.add(timerBarBg);
    
    // Timer bar
    const timerBar = this.add.rectangle(-60, timerY, 120, 8, 0xffd93d);
    timerBar.setOrigin(0, 0.5);
    timerBar.setVisible(false);
    container.add(timerBar);
    
    // Background plate - usunięte
    const background = this.add.rectangle(0, 0, 140, 90, 0x0d1530, 0);
    background.setVisible(false);
    container.add(background);
    
    // Name text - nad awatarem
    const nameText = this.add.text(0, avatarTopY - AVATAR_NAME_OFFSET, 'Puste', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    container.add(nameText);
    
    // Avatar - obraz zamiast kółka, średnica 150px (75% z 200px)
    const avatar = this.add.image(0, AVATAR_CENTER_Y, AVATAR_PRESET_KEYS[0]);
    avatar.setDisplaySize(AVATAR_DIAMETER, AVATAR_DIAMETER);
    avatar.setVisible(false); // Ukryty dopóki gracz nie zasiądzie
    container.add(avatar);
    
    // Chips background - delikatny prostokąt pod tekstem
    const chipsBg = this.add.graphics();
    chipsBg.setPosition(0, avatarBottomY + AVATAR_CHIPS_OFFSET);
    chipsBg.setVisible(false);
    container.add(chipsBg);

    // Chips text - pod awatarem (niżej aby nie nachodzić na highlight ring)
    const chipsText = this.add.text(0, avatarBottomY + AVATAR_CHIPS_OFFSET, '', {
      fontSize: '24px', // Zwiększone z 12px do 24px (2x)
      color: '#ffffff',
      fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5);
    container.add(chipsText);
    
    // Chips icon (small circle) - ukryty (nie używany)
    const chipsIcon = this.add.circle(-35, 42, 8, 0xd4af37);
    chipsIcon.setStrokeStyle(1, 0xffffff);
    chipsIcon.setVisible(false);
    container.add(chipsIcon);
    
    // Status text (fold, all-in, etc.)
    const statusText = this.add.text(0, AVATAR_CENTER_Y, '', {
      fontSize: '12px',
      color: '#ff6b6b',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(statusText);
    
    // Karty gracza (2 karty)
    const cards: CardDisplay[] = [];
    // Dla gracza lokalnego (visualIndex 0) karty większe
    const isLocalPlayer = visualIndex === 0;
    const cardWidth = isLocalPlayer ? 94 : 50; // +25% dla lokalnego gracza
    const cardHeight = isLocalPlayer ? 132 : 70;
    const cardY = isLocalPlayer ? this.getLocalCardY() : this.getOpponentCardY(cardHeight);
    for (let c = 0; c < 2; c++) {
      const cardX = (c - 0.5) * (cardWidth + 5);
      const card = this.createCardDisplay(cardX, cardY, cardWidth, cardHeight);
      container.add(card.container);
      cards.push(card);
    }
    
    // Dealer chip
    const dealerChip = this.createDealerChip();
    dealerChip.setPosition(55, -35);
    dealerChip.setVisible(false);
    container.add(dealerChip);
    
    // Bet container (positioned toward center of table)
    const betContainer = this.add.container(betPos.x, betPos.y);
    
    // Bet chips (visual circles representing chips)
    const betChips: Phaser.GameObjects.Arc[] = [];
    for (let i = 0; i < 5; i++) {
      const chip = this.add.circle(-20 + i * 8, -5 + (i % 2) * 3, 10, this.getChipColor(i));
      chip.setStrokeStyle(1, 0xffffff);
      chip.setVisible(false);
      betContainer.add(chip);
      betChips.push(chip);
    }
    
    // Bet amount text
    const betText = this.add.text(20, 0, '', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0, 0.5);
    betContainer.add(betText);
    
    return {
      container,
      background,
      avatar,
      nameText,
      chipsText,
      chipsBg,
      chipsIcon,
      statusText,
      dealerChip,
      betContainer,
      betText,
      betChips,
      cards,
      highlightRing,
      timerBar,
      timerBarBg,
    };
  }

  /**
   * Zwraca kolor żetonu w zależności od indeksu
   */
  private getChipColor(index: number): number {
    const colors = [0xffffff, 0xff6b6b, 0x6bcf7f, 0x6b9fff, 0xffd93d];
    return colors[index % colors.length];
  }

  /**
   * Aktualizuje wizualizację żetonów zakładu
   */
  private updateBetChips(seat: PlayerSeatUI, amount: number): void {
    // Pokaż żetony proporcjonalnie do kwoty
    const numChips = Math.min(5, Math.ceil(amount / 50));
    seat.betChips.forEach((chip, i) => {
      chip.setVisible(i < numChips && amount > 0);
    });
  }
  private createDealerChip(): Phaser.GameObjects.Container {
    const container = this.add.container(0, 0);
    
    const chip = this.add.circle(0, 0, 15, 0xffffff);
    chip.setStrokeStyle(2, 0x000000);
    container.add(chip);
    
    const text = this.add.text(0, 0, 'D', {
      fontSize: '14px',
      color: '#000000',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(text);
    
    return container;
  }

  private createActionButtons(): void {
    this.actionButtonsContainer = this.add.container(960, 980);
    
    // Reduce button scale and increase spacing for proper 5px gaps
    const buttonScale = 0.40;
    const buttonSpacing = 175;
    const buttons = [
      { key: 'fold', text: 'FOLD', x: -2 * buttonSpacing, color: 0xff6b6b },
      { key: 'check', text: 'CHECK', x: -1 * buttonSpacing, color: 0x6bcf7f },
      { key: 'call', text: 'CALL', x: 0, color: 0x6b9fff },
      { key: 'raise', text: 'RAISE', x: 1 * buttonSpacing, color: 0xffd93d },
      { key: 'allin', text: 'ALL-IN', x: 2 * buttonSpacing, color: 0xff6b6b },
    ];
    
    buttons.forEach((btn) => {
      const buttonBg = this.add.image(btn.x, 0, 'button_small').setScale(buttonScale);
      buttonBg.setInteractive({ useHandCursor: true });
      
      const buttonText = this.add.text(btn.x, 0, btn.text, {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: 'Rajdhani, Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5);
      
      // Hover effect
      buttonBg.on('pointerover', () => buttonBg.setTint(0xcccccc));
      buttonBg.on('pointerout', () => buttonBg.clearTint());
      
      // Click handlers
      buttonBg.on('pointerdown', () => {
        console.log('[GameScene] Button clicked:', btn.key);
        this.onActionButton(btn.key);
      });
      
      this.actionButtonsContainer.add([buttonBg, buttonText]);
      
      // Store references
      switch (btn.key) {
        case 'fold': this.foldBtn = buttonBg; break;
        case 'check': 
          this.checkBtn = buttonBg; 
          this.checkBtnText = buttonText;
          break;
        case 'call': 
          this.callBtn = buttonBg; 
          this.callBtnText = buttonText;
          break;
        case 'raise': this.raiseBtn = buttonBg; break;
        case 'allin': this.allInBtn = buttonBg; break;
      }
    });
  }

  private createRaiseSlider(): void {
    // Action buttons are at Y=980, move box 40px higher
    // Box height = 90px, center Y = 885
    this.raiseSliderContainer = this.add.container(960, 885);
    
    // Background
    const bg = this.add.rectangle(0, 0, 560, 90, 0x0d1530, 0.95);
    bg.setStrokeStyle(2, 0xd4af37);
    this.raiseSliderContainer.add(bg);
    
    // Row 1: Input field with +/- buttons (Y = -22)
    // Decrease button
    this.raiseDecreaseBtn = this.add.rectangle(-130, -22, 40, 32, 0x0d1a3a);
    this.raiseDecreaseBtn.setStrokeStyle(1, 0x6b6bcf);
    this.raiseDecreaseBtn.setInteractive({ useHandCursor: true });
    this.raiseSliderContainer.add(this.raiseDecreaseBtn);

    const minusText = this.add.text(-130, -22, '-', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.raiseSliderContainer.add(minusText);

    // Amount display/input in the middle - clickable text that opens input
    const inputBg = this.add.rectangle(0, -22, 140, 32, 0x050814);
    inputBg.setStrokeStyle(1, 0xd4af37);
    inputBg.setInteractive({ useHandCursor: true });
    this.raiseSliderContainer.add(inputBg);
    
    this.raiseAmountText = this.add.text(0, -22, '0', {
      fontSize: '18px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.raiseSliderContainer.add(this.raiseAmountText);

    // Click on amount to edit manually
    inputBg.on('pointerdown', () => this.openRaiseInputOverlay());

    // Increase button
    this.raiseIncreaseBtn = this.add.rectangle(130, -22, 40, 32, 0x0d1a3a);
    this.raiseIncreaseBtn.setStrokeStyle(1, 0x6b6bcf);
    this.raiseIncreaseBtn.setInteractive({ useHandCursor: true });
    this.raiseSliderContainer.add(this.raiseIncreaseBtn);

    const plusText = this.add.text(130, -22, '+', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.raiseSliderContainer.add(plusText);
    
    // Row 2: Slider with MIN/MAX buttons (Y = 22)
    // Min button - left of slider
    this.raiseMinBtn = this.add.rectangle(-245, 22, 50, 28, 0x2d5a2d);
    this.raiseMinBtn.setStrokeStyle(1, 0x6bcf7f);
    this.raiseMinBtn.setInteractive({ useHandCursor: true });
    this.raiseSliderContainer.add(this.raiseMinBtn);
    
    const minBtnText = this.add.text(-245, 22, 'MIN', {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
    }).setOrigin(0.5);
    this.raiseSliderContainer.add(minBtnText);
    
    // Track - horizontal slider in the middle (width 340)
    this.raiseSliderTrack = this.add.rectangle(0, 22, 340, 8, 0x333333);
    this.raiseSliderContainer.add(this.raiseSliderTrack);
    
    // Slider fill
    this.raiseSlider = this.add.rectangle(-170, 22, 0, 8, 0xd4af37);
    this.raiseSlider.setOrigin(0, 0.5);
    this.raiseSliderContainer.add(this.raiseSlider);
    
    // Handle
    this.raiseSliderHandle = this.add.circle(-170, 22, 12, 0xffffff);
    this.raiseSliderHandle.setStrokeStyle(2, 0xd4af37);
    this.raiseSliderHandle.setInteractive({ useHandCursor: true, draggable: true });
    this.raiseSliderContainer.add(this.raiseSliderHandle);
    
    // Max button - right of slider
    this.raiseMaxBtn = this.add.rectangle(245, 22, 50, 28, 0x5a2d2d);
    this.raiseMaxBtn.setStrokeStyle(1, 0xff6b6b);
    this.raiseMaxBtn.setInteractive({ useHandCursor: true });
    this.raiseSliderContainer.add(this.raiseMaxBtn);
    
    const maxBtnText = this.add.text(245, 22, 'MAX', {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
    }).setOrigin(0.5);
    this.raiseSliderContainer.add(maxBtnText);

    // Legacy DOM input - kept hidden, used only for overlay sync
    this.raiseInput = this.add.dom(0, -100, 'input', `
      width: 1px; height: 1px; opacity: 0; position: absolute; pointer-events: none;
    `, '0');
    this.raiseSliderContainer.add(this.raiseInput);
    
    // Drag handler - slider track is 340px wide (-170 to +170)
    this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject, dragX: number) => {
      if (gameObject === this.raiseSliderHandle) {
        this.raiseSliderTouched = true;
        const clampedX = Phaser.Math.Clamp(dragX, -170, 170);
        const percentage = (clampedX + 170) / 340;
        const newAmount = Math.round(this.minRaise + percentage * (this.maxRaise - this.minRaise));
        this.setRaiseAmount(newAmount);
      }
    });
    
    // Button handlers
    this.raiseDecreaseBtn.on('pointerdown', () => {
      this.raiseSliderTouched = true;
      this.adjustRaiseByStep(-1);
    });
    this.raiseIncreaseBtn.on('pointerdown', () => {
      this.raiseSliderTouched = true;
      this.adjustRaiseByStep(1);
    });
    this.raiseMinBtn.on('pointerdown', () => {
      this.raiseSliderTouched = true;
      this.setRaiseAmount(this.minRaise);
    });
    this.raiseMaxBtn.on('pointerdown', () => {
      this.raiseSliderTouched = true;
      this.setRaiseAmount(this.maxRaise);
    });
    
    // Hover effects
    this.raiseMinBtn.on('pointerover', () => this.raiseMinBtn.setFillStyle(0x3d6a3d));
    this.raiseMinBtn.on('pointerout', () => this.raiseMinBtn.setFillStyle(0x2d5a2d));
    this.raiseMaxBtn.on('pointerover', () => this.raiseMaxBtn.setFillStyle(0x6a3d3d));
    this.raiseMaxBtn.on('pointerout', () => this.raiseMaxBtn.setFillStyle(0x5a2d2d));
    this.raiseDecreaseBtn.on('pointerover', () => this.raiseDecreaseBtn.setFillStyle(0x3b3b6b));
    this.raiseDecreaseBtn.on('pointerout', () => this.raiseDecreaseBtn.setFillStyle(0x0d1a3a));
    this.raiseIncreaseBtn.on('pointerover', () => this.raiseIncreaseBtn.setFillStyle(0x3b3b6b));
    this.raiseIncreaseBtn.on('pointerout', () => this.raiseIncreaseBtn.setFillStyle(0x0d1a3a));
  }

  /**
   * Open an overlay input for manual raise amount entry
   * Uses a positioned HTML input that scales with the game
   */
  private openRaiseInputOverlay(): void {
    // Check if overlay already exists
    if (document.getElementById('raise-input-overlay')) {
      return;
    }

    const canvas = this.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    
    // Calculate the position of the input relative to canvas
    // Container is at (960, 885), input area at relative Y=-22
    const gameWidth = this.cameras.main.width;
    const gameHeight = this.cameras.main.height;
    const scaleX = canvasRect.width / gameWidth;
    const scaleY = canvasRect.height / gameHeight;
    
    const inputCenterX = 960;
    const inputCenterY = 885 - 22; // 863
    
    const screenX = canvasRect.left + inputCenterX * scaleX;
    const screenY = canvasRect.top + inputCenterY * scaleY;
    const inputWidth = 140 * scaleX;
    const inputHeight = 32 * scaleY;
    
    // Create overlay
    const overlay = document.createElement('div');
    overlay.id = 'raise-input-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.3);
      z-index: 10000;
    `;
    
    const input = document.createElement('input');
    input.type = 'number';
    input.value = `${this.currentRaiseAmount}`;
    input.min = `${this.minRaise}`;
    input.max = `${this.maxRaise}`;
    input.step = `${Math.max(1, this.raiseStep)}`;
    input.style.cssText = `
      position: fixed;
      left: ${screenX - inputWidth / 2}px;
      top: ${screenY - inputHeight / 2}px;
      width: ${inputWidth}px;
      height: ${inputHeight}px;
      font-size: ${16 * Math.min(scaleX, scaleY)}px;
      text-align: center;
      color: #d4af37;
      background: #0d0d0d;
      border: 2px solid #d4af37;
      border-radius: 4px;
      outline: none;
      z-index: 10001;
      font-family: 'Rajdhani', Arial, sans-serif;
    `;
    
    overlay.appendChild(input);
    document.body.appendChild(overlay);
    
    input.focus();
    input.select();
    
    const closeOverlay = () => {
      const value = parseInt(input.value, 10);
      if (!Number.isNaN(value)) {
        this.raiseSliderTouched = true;
        this.setRaiseAmount(value);
      }
      overlay.remove();
    };
    
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeOverlay();
      }
    });
    
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        closeOverlay();
      }
    });
    
    input.addEventListener('blur', () => {
      // Small delay to allow click events to process
      setTimeout(() => {
        if (document.getElementById('raise-input-overlay')) {
          closeOverlay();
        }
      }, 100);
    });
  }

  private setRaiseAmount(amount: number): void {
    const normalizedAmount = Math.round(amount);
    const upperBound = Math.max(this.maxRaise, this.minRaise);
    const clampedAmount = Phaser.Math.Clamp(normalizedAmount, this.minRaise, upperBound);
    this.currentRaiseAmount = clampedAmount;
    const range = Math.max(1, upperBound - this.minRaise);
    const percentage = (clampedAmount - this.minRaise) / range;
    // Slider track is 340px wide (-170 to +170)
    const handleX = -170 + percentage * 340;
    this.raiseSliderHandle.x = handleX;
    this.raiseSlider.width = handleX + 170;
    this.raiseAmountText.setText(`${clampedAmount}`);
    this.syncRaiseInput(clampedAmount);
  }

  private adjustRaiseByStep(direction: -1 | 1): void {
    const step = Math.max(1, this.raiseStep);
    const nextAmount = this.currentRaiseAmount + direction * step;
    this.setRaiseAmount(nextAmount);
  }

  private syncRaiseInput(amount: number): void {
    if (!this.raiseInput) {
      return;
    }
    const inputEl = this.raiseInput.node as HTMLInputElement;
    if (document.activeElement !== inputEl) {
      inputEl.value = `${amount}`;
    }
  }

  private updateRaiseInputStep(): void {
    if (!this.raiseInput) {
      return;
    }
    const inputEl = this.raiseInput.node as HTMLInputElement;
    inputEl.step = `${Math.max(1, this.raiseStep)}`;
  }

  private createMenuButton(): void {
    this.menuBtn = this.add.image(100, 50, 'button_small').setScale(0.4);
    this.menuBtn.setInteractive({ useHandCursor: true });
    
    const menuText = this.add.text(100, 50, 'MENU', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    this.menuBtn.on('pointerover', () => this.menuBtn.setTint(0xcccccc));
    this.menuBtn.on('pointerout', () => this.menuBtn.clearTint());
    this.menuBtn.on('pointerdown', () => this.showLeaveConfirm());
  }

  private getTableBgKey(): string {
    switch (this.deckSettings.tableTheme) {
      case 'blue':
        return 'table_bg_blue';
      case 'red':
        return 'table_bg_red';
      default:
        return 'table_bg';
    }
  }

  private layoutTopButtons(): void {
    if (!this.settingsButton) {
      return;
    }

    const margin = 20;
    const canvasWidth = this.scale?.width || 1920;
    const baseHeight = this.menuBtn?.displayHeight || 50;

    this.settingsButton.setDisplaySize(baseHeight, baseHeight);
    const settingsWidth = this.settingsButton.displayWidth;
    const topY = this.menuBtn?.y ?? margin + baseHeight / 2;
    const settingsX = canvasWidth - margin - (settingsWidth / 2);

    this.settingsButton.setPosition(settingsX, topY);
  }

  private createSettingsButton(): void {
    this.settingsButton = this.add.image(0, 0, 'settings_button');
    this.settingsButton.setDepth(101);
    this.settingsButton.setInteractive({ useHandCursor: true });

    if (this.menuBtn?.displayHeight) {
      this.settingsButton.setDisplaySize(this.menuBtn.displayHeight, this.menuBtn.displayHeight);
    }

    this.settingsButton.on('pointerover', () => this.settingsButton.setTint(0xcccccc));
    this.settingsButton.on('pointerout', () => this.settingsButton.clearTint());
    this.settingsButton.on('pointerdown', () => this.showSettingsPanel());
  }

  private createSettingsPanel(): void {
    this.settingsPanelGroup = this.add.container(0, 0);
    this.settingsPanelGroup.setDepth(1000);
    this.settingsPanelGroup.setVisible(false);

    const overlay = this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.65);
    overlay.setInteractive();
    overlay.on('pointerdown', () => this.hideSettingsPanel());

    const panelBg = this.add.rectangle(960, 540, 640, 560, 0x050814, 0.95);
    panelBg.setStrokeStyle(2, 0xd4af37);

    const title = this.add.text(960, 360, 'USTAWIENIA', {
      fontSize: '30px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);

    const subtitle = this.add.text(960, 395, 'Dostosuj głośność, talię i stół', {
      fontSize: '16px',
      color: '#cccccc',
      fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5);

    const deckLabel = this.add.text(960, 435, 'TALIA', {
      fontSize: '16px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const classicBtn = this.add.image(820, 480, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.45);
    const altBtn = this.add.image(1100, 480, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.45);
    const classicText = this.add.text(820, 480, 'KLASYCZNA', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    const altText = this.add.text(1100, 480, 'ALTERNATYWNA', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    classicBtn.on('pointerover', () => classicBtn.setTint(0xcccccc));
    classicBtn.on('pointerout', () => classicBtn.clearTint());
    classicBtn.on('pointerdown', () => this.setDeck('classic'));

    altBtn.on('pointerover', () => altBtn.setTint(0xcccccc));
    altBtn.on('pointerout', () => altBtn.clearTint());
    altBtn.on('pointerdown', () => this.setDeck('alternative'));

    this.deckButtons = { classic: classicBtn, alternative: altBtn };
    this.deckButtonLabels = { classic: classicText, alternative: altText };
    this.updateDeckButtons();

    const tableLabel = this.add.text(960, 520, 'STÓŁ', {
      fontSize: '16px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const tableGreenBtn = this.add.image(730, 565, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.4);
    const tableBlueBtn = this.add.image(960, 565, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.4);
    const tableRedBtn = this.add.image(1190, 565, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.4);
    const tableGreenText = this.add.text(730, 565, 'ZIELONY', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    const tableBlueText = this.add.text(960, 565, 'NIEBIESKI', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    const tableRedText = this.add.text(1190, 565, 'CZERWONY', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    tableGreenBtn.on('pointerover', () => tableGreenBtn.setTint(0xcccccc));
    tableGreenBtn.on('pointerout', () => tableGreenBtn.clearTint());
    tableGreenBtn.on('pointerdown', () => this.setTableTheme('green'));

    tableBlueBtn.on('pointerover', () => tableBlueBtn.setTint(0xcccccc));
    tableBlueBtn.on('pointerout', () => tableBlueBtn.clearTint());
    tableBlueBtn.on('pointerdown', () => this.setTableTheme('blue'));

    tableRedBtn.on('pointerover', () => tableRedBtn.setTint(0xcccccc));
    tableRedBtn.on('pointerout', () => tableRedBtn.clearTint());
    tableRedBtn.on('pointerdown', () => this.setTableTheme('red'));

    this.tableButtons = { green: tableGreenBtn, blue: tableBlueBtn, red: tableRedBtn };
    this.tableButtonLabels = { green: tableGreenText, blue: tableBlueText, red: tableRedText };
    this.updateTableButtons();

    this.sfxSlider = this.createVolumeSlider(960, 635, 'SFX', this.audioSettings.sfxVolume, (value) => this.setSfxVolume(value));
    this.musicSlider = this.createVolumeSlider(960, 705, 'MUZYKA', this.audioSettings.musicVolume, (value) => this.setMusicVolume(value));

    const closeBtn = this.add.image(960, 770, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.4);
    const closeText = this.add.text(960, 770, 'ZAMKNIJ', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    closeBtn.on('pointerover', () => closeBtn.setTint(0xcccccc));
    closeBtn.on('pointerout', () => closeBtn.clearTint());
    closeBtn.on('pointerdown', () => this.hideSettingsPanel());

    this.settingsPanelGroup.add([
      overlay,
      panelBg,
      title,
      subtitle,
      deckLabel,
      classicBtn,
      altBtn,
      classicText,
      altText,
      tableLabel,
      tableGreenBtn,
      tableBlueBtn,
      tableRedBtn,
      tableGreenText,
      tableBlueText,
      tableRedText,
      this.sfxSlider.container,
      this.musicSlider.container,
      closeBtn,
      closeText,
    ]);
  }

  private createEmojiWheel(): void {
    const canvasWidth = this.scale?.width || 1920;
    const canvasHeight = this.scale?.height || 1080;
    const wheelDepth = 120;
    const wheelCenterX = canvasWidth - EMOJI_WHEEL_RADIUS - EMOJI_WHEEL_MARGIN;
    const wheelCenterY = canvasHeight - EMOJI_WHEEL_RADIUS - EMOJI_WHEEL_MARGIN;
    const toggleX = wheelCenterX;
    const toggleY = wheelCenterY;

    this.emojiWheelContainer = this.add.container(wheelCenterX, wheelCenterY);
    this.emojiWheelContainer.setDepth(wheelDepth);
    this.emojiWheelContainer.setVisible(false);

    const wheelBg = this.add.graphics();
    const wheelPoints: Phaser.Math.Vector2[] = [];
    for (let i = 0; i < 8; i += 1) {
      const angle = Phaser.Math.DegToRad(22.5 + i * 45);
      wheelPoints.push(new Phaser.Math.Vector2(
        Math.cos(angle) * EMOJI_WHEEL_RADIUS,
        Math.sin(angle) * EMOJI_WHEEL_RADIUS,
      ));
    }
    wheelBg.fillStyle(0x000000, 0.7);
    wheelBg.fillPoints(wheelPoints, true);
    this.emojiWheelContainer.add(wheelBg);

    QUICK_EMOJI_SET.forEach((emoji, index) => {
      const angle = Phaser.Math.DegToRad(-90 + index * 45);
      const x = Math.cos(angle) * EMOJI_WHEEL_ITEM_RADIUS;
      const y = Math.sin(angle) * EMOJI_WHEEL_ITEM_RADIUS;
      const emojiText = this.add.text(x, y, emoji, {
        fontSize: '32px',
        fontFamily: EMOJI_FONT_STACK,
      }).setOrigin(0.5);
      emojiText.setInteractive({ useHandCursor: true });
      emojiText.on('pointerover', () => emojiText.setScale(1.1));
      emojiText.on('pointerout', () => emojiText.setScale(1));
      emojiText.on('pointerdown', () => this.handleEmojiSelect(emoji));
      this.emojiWheelContainer.add(emojiText);
    });

    this.emojiToggleContainer = this.add.container(toggleX, toggleY);
    this.emojiToggleContainer.setDepth(wheelDepth);

    this.emojiToggleBg = this.add.circle(0, 0, EMOJI_TOGGLE_RADIUS, 0x000000, 0.7);
    this.emojiToggleBg.setStrokeStyle(2, 0xd4af37);
    this.emojiToggleBg.setInteractive({ useHandCursor: true });
    this.emojiToggleBg.on('pointerover', () => this.emojiToggleBg.setFillStyle(0x050814, 0.7));
    this.emojiToggleBg.on('pointerout', () => this.emojiToggleBg.setFillStyle(0x000000, 0.7));

    const toggleIcon = this.add.text(0, 0, '😊', {
      fontSize: '22px',
      fontFamily: EMOJI_FONT_STACK,
    }).setOrigin(0.5);
    toggleIcon.setInteractive({ useHandCursor: true });

    const toggleHandler = () => this.toggleEmojiWheel();
    this.emojiToggleBg.on('pointerdown', toggleHandler);
    toggleIcon.on('pointerdown', toggleHandler);

    this.emojiToggleContainer.add([this.emojiToggleBg, toggleIcon]);
  }

  private toggleEmojiWheel(): void {
    this.setEmojiWheelOpen(!this.emojiWheelOpen);
  }

  private setEmojiWheelOpen(open: boolean): void {
    this.emojiWheelOpen = open;
    if (this.emojiWheelContainer) {
      this.emojiWheelContainer.setVisible(open);
    }
    if (this.emojiToggleBg) {
      this.emojiToggleBg.setStrokeStyle(2, open ? 0xffd93d : 0xd4af37);
    }
  }

  private handleEmojiSelect(emoji: string): void {
    this.setEmojiWheelOpen(false);
    void this.sendEmojiReaction(emoji);
  }

  private async sendEmojiReaction(emoji: string): Promise<void> {
    const username = this.getLocalUsername();
    this.lastEmojiSent = { emoji, at: Date.now() };
    this.showEmojiFlyer(username, emoji);

    const result = await pokerClient.sendEmojiReaction(emoji, username, this.localPlayerId);
    if (!result.success) {
      console.warn('[GameScene] Emoji reaction send failed:', result.error);
    }
  }

  private handleEmojiReaction(event: EmojiReactionEvent): void {
    if (!event?.emoji) return;
    const eventData = event as EmojiReactionEvent & { playerId?: string; playerName?: string };
    if (this.shouldSkipEmojiEcho(eventData)) return;
    const username =
      eventData.username ||
      eventData.playerName ||
      this.getUsernameById(eventData.userId || eventData.playerId) ||
      'Gracz';
    this.showEmojiFlyer(username, eventData.emoji);
  }

  private shouldSkipEmojiEcho(event: EmojiReactionEvent & { playerId?: string; playerName?: string }): boolean {
    if (!this.lastEmojiSent) return false;
    const isLocal =
      (event.userId && event.userId === this.localPlayerId) ||
      (event.playerId && event.playerId === this.localPlayerId) ||
      (event.username && event.username === pokerClient.username) ||
      (event.playerName && event.playerName === pokerClient.username);
    if (!isLocal) return false;
    return event.emoji === this.lastEmojiSent.emoji && Date.now() - this.lastEmojiSent.at < 2500;
  }

  private getLocalUsername(): string {
    return pokerClient.username || this.getUsernameById(this.localPlayerId) || 'Gracz';
  }

  private getUsernameById(userId?: string): string | null {
    if (!userId) return null;
    const player = this.tableState?.players?.find((p) => p.userId === userId);
    return player?.username || null;
  }

  private showEmojiFlyer(username: string, emoji: string): void {
    const canvasWidth = this.scale?.width || 1920;
    const canvasHeight = this.scale?.height || 1080;
    const startX = canvasWidth - 160;
    const startY = canvasHeight + 40;
    const driftX = Phaser.Math.Between(-25, 20);

    const container = this.add.container(startX + driftX, startY);
    container.setDepth(160);

    const nameText = this.add.text(-10, 0, `${username}:`, {
      fontSize: '20px',
      color: '#ffd93d',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(1, 0.5);

    const emojiText = this.add.text(10, 0, emoji, {
      fontSize: '56px',
      fontFamily: EMOJI_FONT_STACK,
    }).setOrigin(0, 0.5);

    container.add([nameText, emojiText]);

    this.tweens.add({
      targets: container,
      y: -80,
      duration: 4100,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.tweens.add({
          targets: container,
          alpha: 0,
          duration: 300,
          ease: 'Sine.easeOut',
          onComplete: () => container.destroy(),
        });
      },
    });
  }

  private createVolumeSlider(
    x: number,
    y: number,
    label: string,
    initialValue: number,
    onChange: (value: number) => void,
  ): VolumeSlider {
    const sliderWidth = 320;
    const trackX = -(sliderWidth / 2);
    const container = this.add.container(x, y);

    const labelText = this.add.text(0, -30, label, {
      fontSize: '16px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const track = this.add.rectangle(trackX, 8, sliderWidth, 8, 0x222222, 0.9)
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });

    const fill = this.add.rectangle(trackX, 8, sliderWidth * initialValue, 8, 0xd4af37, 1)
      .setOrigin(0, 0.5);

    const handle = this.add.circle(trackX + (sliderWidth * initialValue), 8, 12, 0xd4af37)
      .setInteractive({ useHandCursor: true });

    const valueText = this.add.text(trackX + sliderWidth + 35, 8, `${Math.round(initialValue * 100)}%`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5);

    container.add([labelText, track, fill, handle, valueText]);

    const applyValue = (value: number) => {
      const clamped = Phaser.Math.Clamp(value, 0, 1);
      fill.width = sliderWidth * clamped;
      handle.x = trackX + (sliderWidth * clamped);
      valueText.setText(`${Math.round(clamped * 100)}%`);
    };

    const worldXFromPointer = (p: Phaser.Input.Pointer): number => (p.worldX ?? p.x ?? 0);

    const updateFromPointer = (pointer: Phaser.Input.Pointer) => {
      const bounds = track.getBounds();
      const worldX = worldXFromPointer(pointer);
      const localX = Phaser.Math.Clamp(worldX - bounds.x, 0, bounds.width);
      const value = localX / bounds.width;
      applyValue(value);
      onChange(value);
    };

    track.on('pointerdown', (pointer: Phaser.Input.Pointer) => updateFromPointer(pointer));
    this.input.setDraggable(handle);
    handle.on('drag', (pointer: Phaser.Input.Pointer) => updateFromPointer(pointer));
    handle.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      updateFromPointer(pointer);
    });
    handle.on('pointerover', () => handle.setScale(1.05));
    handle.on('pointerout', () => handle.setScale(1));

    return {
      container,
      updateFromValue: applyValue,
    };
  }

  private showSettingsPanel(): void {
    if (!this.settingsPanelGroup) return;
    this.settingsPanelGroup.setVisible(true);
    this.sfxSlider?.updateFromValue(this.audioSettings.sfxVolume);
    this.musicSlider?.updateFromValue(this.audioSettings.musicVolume);
    this.updateDeckButtons();
    this.updateTableButtons();
  }

  private hideSettingsPanel(): void {
    if (!this.settingsPanelGroup) return;
    this.settingsPanelGroup.setVisible(false);
  }

  private updateDeckButtons(): void {
    if (!this.deckButtons || !this.deckButtonLabels) return;
    const isClassic = this.deckSettings.selectedDeck === 'classic';
    this.deckButtons.classic.setTexture('button_small');
    this.deckButtons.alternative.setTexture('button_small');
    this.deckButtonLabels.classic.setColor(isClassic ? '#d4af37' : '#ffffff');
    this.deckButtonLabels.alternative.setColor(isClassic ? '#ffffff' : '#d4af37');
  }

  private updateTableButtons(): void {
    if (!this.tableButtons || !this.tableButtonLabels) return;
    const selectedTheme = this.deckSettings.tableTheme;
    this.tableButtons.green.setTexture('button_small');
    this.tableButtons.blue.setTexture('button_small');
    this.tableButtons.red.setTexture('button_small');
    this.tableButtonLabels.green.setColor(selectedTheme === 'green' ? '#d4af37' : '#ffffff');
    this.tableButtonLabels.blue.setColor(selectedTheme === 'blue' ? '#d4af37' : '#ffffff');
    this.tableButtonLabels.red.setColor(selectedTheme === 'red' ? '#d4af37' : '#ffffff');
  }

  private setDeck(deckId: DeckId): void {
    if (this.deckSettings.selectedDeck === deckId) return;
    this.deckSettings.selectedDeck = deckId;
    saveDeckSettings(this.deckSettings);
    const deckBackKeys = CARD_BACK_KEYS_BY_DECK[deckId] || [];
    this.availableCardBackKeys = deckBackKeys.length > 0 ? deckBackKeys : CARD_BACK_KEYS_BY_DECK.classic;
    this.cardBackKey = this.pickRandomCardBack();
    this.cardFaceMap = CARD_FACE_MAPS[deckId] || CARD_FACE_MAPS.classic;
    this.updateDeckButtons();
    this.refreshCardFaces();
    this.refreshGlowUnderlays();
  }

  private setTableTheme(theme: TableTheme): void {
    if (this.deckSettings.tableTheme === theme) return;
    this.deckSettings.tableTheme = theme;
    saveDeckSettings(this.deckSettings);
    this.updateTableButtons();
    if (this.tableBg) {
      this.tableBg.setTexture(this.getTableBgKey());
      this.tableBg.setDisplaySize(1920, 1080);
    }
  }

  private refreshCardFaces(): void {
    if (!this.tableState) return;
    this.updateCommunityCards(this.tableState.communityCards || [], false);
    this.updatePlayerSeats(this.tableState, false);
  }

  private setSfxVolume(value: number): void {
    this.audioSettings.sfxVolume = Phaser.Math.Clamp(value, 0, 1);
    saveAudioSettings(this.audioSettings);
  }

  private setMusicVolume(value: number): void {
    this.audioSettings.musicVolume = Phaser.Math.Clamp(value, 0, 1);
    saveAudioSettings(this.audioSettings);
  }

  /**
   * Format number with Polish formatting (space every 3 digits)
   */
  private formatPolishNumber(num: number): string {
    const rounded = Math.round(num);
    return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  private isLoosePlayTable(state: PokerTableState | null = this.tableState): boolean {
    return !!(state?.loosePlay ?? state?.isLoosePlay);
  }

  private applyLoosePlayState(state: PokerTableState | null = this.tableState): void {
    const isLoosePlay = this.isLoosePlayTable(state);
    this.loosePlayActive = isLoosePlay;

    if (this.pcBalanceContainer) {
      this.pcBalanceContainer.setVisible(!isLoosePlay);
    }

    if (isLoosePlay) {
      pokerClient.onCoinBalanceChanged = null;
    } else {
      pokerClient.onCoinBalanceChanged = (balance: number) => {
        this.updatePCBalance(balance);
      };
      this.updatePCBalance(pokerClient.coinBalance);
    }
  }

  private getBuyinUnitLabel(): string {
    return this.isLoosePlayTable() ? 'żetonów' : '';
  }

  private formatBuyinAmount(amount: number): string {
    const unitLabel = this.getBuyinUnitLabel();
    return unitLabel ? `${amount} ${unitLabel}` : `${amount}`;
  }

  /**
   * Create PC balance display in top-left corner (below menu button)
   */
  private createPCBalanceDisplay(): void {
    this.pcBalanceContainer = this.add.container(70, 140);
    this.pcBalanceContainer.setDepth(100);

    // Background asset (256x256 scaled to 50% = 128px for game scene)
    const bgImage = this.add.image(45, 45, 'pc_acc').setOrigin(0.5);
    bgImage.setDisplaySize(90, 90);
    
    // Label "Saldo" above balance
    const saldoLabel = this.add.text(45, 20, 'Saldo', {
      fontSize: '11px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 1,
      align: 'center',
    }).setOrigin(0.5);
    
    // Balance text - centered on the asset
    this.pcBalanceText = this.add.text(45, 45, '0', {
      fontSize: '17px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 1,
      align: 'center',
    }).setOrigin(0.5);
    
    this.pcBalanceContainer.add([bgImage, saldoLabel, this.pcBalanceText]);

    this.pcBalanceContainer.setVisible(!this.isLoosePlayTable());

    // Initialize with current balance
    const client = (window as any).pokerClient;
    if (client) {
      this.updatePCBalance(client.coinBalance);
    }
  }

  /**
   * Update PC balance display
   */
  private updatePCBalance(balance: number): void {
    if (this.pcBalanceText) {
      this.pcBalanceText.setText(this.formatPolishNumber(balance));
    }
  }

  private setupClientEvents(): void {
    // Ustawienie handlerów dla GameScene
    pokerClient.onLog = (msg) => {
      // Log handled by PokerClient
    };
    
    pokerClient.onError = (msg) => {
      console.error('[GameScene Client]', msg);
    };

    pokerClient.onPokerSettlementError = (event) => {
      const message = event.manualReconciliationRequired
        ? 'Wypłata wymaga ręcznego sprawdzenia. Skontaktuj się z administracją.'
        : event.retryScheduled
          ? 'Wypłata nie powiodła się. System ponowi próbę.'
          : 'Wystąpił problem z rozliczeniem żetonów.';
      this.statusText.setText(message).setColor('#ff6b6b').setVisible(true);
    };
    
    pokerClient.onTableStateChanged = (state) => {
      this.queueTableStateUpdate(state);
    };
    
    pokerClient.onHandStarted = (event) => {
      // Reset pending sit-in flag when new hand starts
      this.pendingSitIn = false;
      // Reset per-hand tracking (bets/folds/all-ins)
      this.lastBetByUserId.clear();
      this.lastFoldStateByUserId.clear();
      this.lastAllInStateByUserId.clear();
      this.clearWinnerAnnouncements();
      this.clearBustedMessageQueue();
      this.lastShowdownAtMs = null;

      // SFX: quiet shuffle at the start of a hand
      this.playSfxByPrefixes(['card-shuffle'], { volume: 0.14, rateLimitKey: 'hand_start', minIntervalMs: 1500 });
      this.statusText.setText('Nowa ręka rozpoczęta!');
      this.time.delayedCall(2000, () => this.statusText.setText(''));
      // Odśwież stan stołu
      this.refreshTableState();
    };
    
    pokerClient.onShowdown = (event) => {
      // SFX: fan/spread cards on showdown
      this.playSfxByPrefixes(['card-fan'], { volume: 0.16, rateLimitKey: 'showdown', minIntervalMs: 1500 });
      this.handleShowdown(event);
    };
    
    pokerClient.onHandWinner = (event) => {
      // Znajdź nazwę gracza po ID
      const winner = this.tableState?.players?.find(p => p.userId === event.playerId);
      const winnerName = winner?.username || event.playerName || 'Gracz';
      const amount = event.amount || 0;
      const handDescription = event.reason === 'all_folded' ? '(wszyscy spasowali)' : '';
      
      // Wyświetl komunikat o zwycięzcy
      this.showWinnerMessage(winnerName, amount, handDescription);

      // SFX: pot/chips collision on win
      this.playSfxByPrefixes(['chips-collide', 'chips-stack'], { volume: 0.24, rateLimitKey: 'hand_winner', minIntervalMs: 1500 });
      
      // Odśwież stan po chwili
      this.time.delayedCall(1000, () => this.refreshTableState());
    };
    
    pokerClient.onPlayerAction = (event) => {
      // Użyj tableState z eventu jeśli dostępny
      if (event?.tableState) {
        this.queueTableStateUpdate(event.tableState);
      } else {
        // We often receive a duplicate lightweight action event right after a full tableState update.
        // Avoid immediately re-fetching and re-applying UI, which can cause visible flicker.
        const now = Date.now();
        if (now - this.lastTableStateAppliedAt < 350) {
          return;
        }
        this.refreshTableState();
      }
    };
    
    pokerClient.onPlayerJoined = (event) => {
      this.playSfxByPrefixes(['chips-handle'], { volume: 0.12, rateLimitKey: 'player_join', minIntervalMs: 450 });
      this.refreshTableState();
    };
    
    pokerClient.onPlayerLeft = (event) => {
      this.playSfxByPrefixes(['chips-handle'], { volume: 0.12, rateLimitKey: 'player_left', minIntervalMs: 450 });
      this.refreshTableState();
    };

    pokerClient.onTableLeft = (event) => {
      if (event.tableId !== this.tableState?.tableId) return;
      this.tableState = null;
      this.playerSeats.forEach((seat) => seat.container.setVisible(false));
      this.setActionButtonsVisible(false);
      this.raiseSliderContainer.setVisible(false);
      this.statusText.setText('Opuszczono stół.').setColor('#e8f4f8').setVisible(true);
    };

    pokerClient.onEmojiReceived = (event) => {
      this.handleEmojiReaction(event);
    };

    pokerClient.onPlayerForcedSitOut = (event) => {
      const playerName = event.playerName || 'Gracz';
      const minRequired = event.minimumRequired || 0;
      const stack = event.stack ?? 0;

      const message = `💤 ${playerName} został usadzony na przerwie (stack ${stack} 💰, minimum ${minRequired} 💰)`;

      // SFX: subtle chips handle when someone is forced sit out
      this.playSfxByPrefixes(['chips-handle'], { volume: 0.12, rateLimitKey: 'forced_sitout', minIntervalMs: 1200 });

      const forcedText = this.add.text(960, 450, message, {
        fontSize: '28px',
        color: '#ffd93d',
        fontFamily: 'Rajdhani, Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: { x: 20, y: 10 },
        align: 'center',
      }).setOrigin(0.5);
      
      this.tweens.add({
        targets: forcedText,
        alpha: 0,
        y: 400,
        duration: 2000,
        delay: 2500,
        onComplete: () => forcedText.destroy()
      });

      if (event.playerId === this.localPlayerId) {
        this.forceSitOutDueToStack = true;
        this.minimumStackToReturn = minRequired;
        this.isSittingOut = true;
        this.pendingSitIn = false;
        this.statusText.setText(`Masz za mało żetonów (min ${minRequired} 💰). Opuść stół lub dokup buy-in i dołącz ponownie.`).setColor('#ffd93d');
        this.showForcedSitoutPrompt(minRequired);
      }

      if (event.tableState) {
        this.tableState = event.tableState;
        this.updateTableUI(event.tableState);
      } else {
        this.refreshTableState();
      }
    };
    
    pokerClient.onPlayerBusted = (event) => {
      const playerName = event.playerName || 'Gracz';
      const reason = event.reason || 'busted';
      
      // Określ komunikat w zależności od powodu
      let message = '';
      if (reason === 'insufficient') {
        const minRequired = event.minimumRequired || 0;
        message = `💸 ${playerName} ma zbyt mało żetonów!\n(minimum: ${minRequired} 💰)`;
      } else {
        message = `💸 ${playerName} stracił wszystkie żetony!`;
      }
      
      // Wyświetl komunikat
      if (reason === 'insufficient') {
        const bustedText = this.add.text(960, 450, message, {
          fontSize: '28px',
          color: '#ff6b6b',
          fontFamily: 'Rajdhani, Arial, sans-serif',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 3,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: { x: 20, y: 10 },
          align: 'center',
        }).setOrigin(0.5);
        
        // Animacja - zanikanie
        this.tweens.add({
          targets: bustedText,
          alpha: 0,
          y: 400,
          duration: 2000,
          delay: 2500,
          onComplete: () => bustedText.destroy()
        });
      } else {
        this.enqueueBustedMessage(message);
      }

      // SFX: quiet chips movement on bust (avoid loud collision spam)
      this.playSfxByPrefixes(['chips-handle', 'chips-stack'], { volume: 0.16, rateLimitKey: 'player_busted', minIntervalMs: 1500 });
      
      // Jeśli to lokalny gracz, reaguj zależnie od powodu
      if (event.playerId === this.localPlayerId) {
        if (reason === 'insufficient') {
          const minRequired = event.minimumRequired || 0;
          this.forceSitOutDueToStack = true;
          this.minimumStackToReturn = minRequired;
          this.isSittingOut = true;
          this.pendingSitIn = false;
          this.statusText.setText(`Masz zbyt mało żetonów (minimum ${minRequired} 💰). Zostałeś posadzony na przerwie.`).setColor('#ffd93d');
          this.showForcedSitoutPrompt(minRequired);
        } else {
          this.statusText.setText('Straciłeś wszystkie żetony!').setColor('#ff6b6b');
          this.setActionButtonsVisible(false);
          this.raiseSliderContainer.setVisible(false);
          this.manualBuyinOnly = true;
          if (this.buyinPopupGroup?.visible) {
            this.buyinPopupGroup.setVisible(false);
            this.buyinPopupShown = false;
          }
          this.showBustedOptionsPopup();
        }
      } else {
        // Odśwież stan stołu
        this.refreshTableState();
      }
    };
    
    pokerClient.onDisconnected = () => {
      this.stopPolling();
      this.statusText.setText('Rozłączono z serwerem!').setColor('#ff6b6b');
      this.time.delayedCall(2000, () => this.scene.start('MenuScene'));
    };
    
    this.applyLoosePlayState(this.tableState);
    
    // Od razu pobierz aktualny stan stołu
    this.refreshTableState();
    
    // Start polling - backend nie wysyła zdarzeń gry poprawnie, więc musimy odpytywać
    this.startPolling();
  }

  /**
   * Start polling for game state updates
   * Backend ma bug - nie wysyła zdarzeń gry do klientów, więc musimy regularnie odpytywać
   */
  private startPolling(): void {
    if (this.pollingTimer) {
      this.pollingTimer.destroy();
    }
    
    this.pollingTimer = this.time.addEvent({
      delay: 2000, // Poll every 2 seconds
      callback: () => {
        this.refreshTableState();
      },
      loop: true,
    });
  }

  /**
   * Stop polling
   */
  private stopPolling(): void {
    if (this.pollingTimer) {
      this.pollingTimer.destroy();
      this.pollingTimer = null;
    }
  }

  private async refreshTableState(): Promise<void> {
    if (!pokerClient.tableId) return;

    if (this.refreshInFlight) {
      this.refreshQueued = true;
      return;
    }
    this.refreshInFlight = true;
    
    try {
      const state = await pokerClient.getTableState(pokerClient.tableId);
      if (state) {
        this.queueTableStateUpdate(state);
      }
    } finally {
      this.refreshInFlight = false;
      if (this.refreshQueued) {
        this.refreshQueued = false;
        this.time.delayedCall(0, () => this.refreshTableState());
      }
    }
  }

  private updateTableUI(state: PokerTableState): void {
    this.applyLoosePlayState(state);
    const phase = (state.currentPhase || state.phase || 'waiting') as string;
    const currentHandNumber = this.parseHandNumber((state as any).currentHandNumber);
    const allowAnimations = this.animationsReady;
    this.animationsReady = true;
    const previousCommunityCount = this.lastCommunityCards.length;
    const communityCardsCount = state.communityCards?.length || 0;
    // Backend can occasionally return a transient shorter communityCards array during polling.
    // Clearing per-player card state on *any* decrease causes hole cards to be treated as "new"
    // and re-animated, which looks like flickering when turns switch.
    const handNumberChanged =
      currentHandNumber !== null && this.lastHandNumber !== null && currentHandNumber !== this.lastHandNumber;
    const isResetPhase =
      phase === 'waiting' || phase === 'preflop' || phase === 'pre_flop' || phase === 'hand_complete' || phase === 'ended';

    // If the server started a new hand, reset any cached UI state immediately.
    if (handNumberChanged) {
      this.clearHandUiCaches(`handNumberChanged ${this.lastHandNumber} -> ${currentHandNumber}`);
    }

    // Backend can occasionally return a transient shorter communityCards array during polling.
    // Only treat decreases as a reset when we also see a true reset context.
    if (communityCardsCount < previousCommunityCount && isResetPhase) {
      this.clearHandUiCaches(`communityCards decreased ${previousCommunityCount} -> ${communityCardsCount} in phase=${phase}`);
    }
    
    // Extra safety: when a new hand begins (pre-flop + empty board), clear any leftover showdown reveals.
    // This covers cases where currentHandNumber is missing/mis-typed and prevents "stuck" revealed cards.
    if (
      communityCardsCount === 0 &&
      (phase === 'preflop' || phase === 'pre_flop') &&
      (this.revealedCards.size > 0 || this.lastCommunityCards.length > 0)
    ) {
      this.lastHandId = '';
      this.clearHandUiCaches('preflop with empty board');
    }

    if (currentHandNumber !== null) {
      this.lastHandNumber = currentHandNumber;
    }
    
    // Jeśli jest showdown, zapisz karty graczy
    if (phase === 'showdown') {
      state.players.forEach(player => {
        if (player.cards && player.cards.length > 0 && !player.isFolded) {
          this.revealedCards.set(player.userId, player.cards);
        }
      });
    }
    
    // Update pot
    this.potText.setText(state.pot.toString());
    
    // Update current bet display (top right)
    this.currentBetText.setText(state.currentBet.toString());
    
    // Update phase (use currentPhase from API)
    const playerCount = state.players?.length || 0;
    
    // Pokaż specjalny tekst gdy czekamy na graczy
    if (phase === 'waiting') {
      if (playerCount < 2) {
        this.phaseText.setText(`Oczekiwanie na graczy (${playerCount}/2 min.)`);
      } else {
        this.phaseText.setText(`Gra się zaraz rozpocznie... (${playerCount} graczy)`);
      }
    } else {
      this.phaseText.setText(this.getPhaseText(phase));
    }

    this.updateFreshStartState(state);

    // Update START vote overlay (waiting-for-first-hand)
    this.updateStartOverlay(state);
    
    // Update community cards
    this.updateCommunityCards(state.communityCards, allowAnimations);
    
    // Update player seats
    this.updatePlayerSeats(state, allowAnimations);
    
    // Update action buttons visibility
    this.updateActionButtons(state);
    
    // Check and handle sitting state
    this.checkSittingState(state);

    // If everyone else is all-in/folded and only we can act, don't force manual CHECKs.
    // Auto-check to run out remaining streets and reach showdown.
    this.maybeAutoFastForwardToShowdown(state);

    // Highlight najlepszy układ dla lokalnego gracza (od flopa)
    this.highlightBestHand(state);
  }

  private maybeAutoFastForwardToShowdown(state: PokerTableState): void {
    const phase = state.currentPhase || state.phase || 'waiting';
    const isActivePhase = phase !== 'waiting' && phase !== 'showdown' && phase !== 'ended' && phase !== 'hand_complete';
    if (!isActivePhase) return;

    const handNumber = typeof state.currentHandNumber === 'number' ? state.currentHandNumber : null;
    if (this.autoFastForwardHandNumber !== handNumber) {
      this.autoFastForwardHandNumber = handNumber;
      this.autoFastForwardChecksThisHand = 0;
      this.autoFastForwardPending = false;
      this.autoFastForwardLastAt = 0;
    }

    // Hard cap to avoid infinite loops if backend gets stuck.
    if (this.autoFastForwardChecksThisHand >= 12) return;

    const localPlayer = state.players.find(p => p.userId === this.localPlayerId);
    if (!localPlayer) return;
    if (localPlayer.isFolded || localPlayer.isAllIn || localPlayer.isSittingOut) return;

    const activePlayerSeat = state.activePlayerSeat ?? state.currentPlayerPosition ?? -1;
    const localSeat = localPlayer.seatNumber ?? localPlayer.position ?? -1;
    if (activePlayerSeat < 0 || localSeat < 0) return;
    if (activePlayerSeat !== localSeat) return;

    // Determine if only one player (local) can still take actions.
    const inHandPlayers = state.players.filter(p => !p.isFolded && !p.isSittingOut);
    if (inHandPlayers.length <= 1) return;

    const canActPlayers = inHandPlayers.filter(p => {
      const stack = p.stack ?? p.chips ?? 0;
      return !p.isAllIn && stack > 0;
    });

    if (canActPlayers.length !== 1) return;
    if (canActPlayers[0].userId !== this.localPlayerId) return;

    // Only auto-check when there is no decision (no call amount).
    const localBet = localPlayer.currentBet ?? localPlayer.bet ?? 0;
    const callAmount = (state.currentBet || 0) - localBet;
    if (callAmount > 0) return;

    // Avoid interfering with manual actions.
    if (this.actionInProgress || this.autoFastForwardPending) return;

    const now = Date.now();
    if (now - this.autoFastForwardLastAt < 450) return;
    this.autoFastForwardLastAt = now;
    this.autoFastForwardPending = true;
    this.autoFastForwardChecksThisHand += 1;

    // Hide UI immediately; we'll re-open it if the state still requires player action.
    this.setActionButtonsVisible(false);
    this.raiseSliderContainer.setVisible(false);

    this.time.delayedCall(80, async () => {
      try {
        this.actionInProgress = true;
        const result = await pokerClient.check();
        if (result && !result.success) {
          console.warn('[AutoFastForward] check failed:', result.error);
        }
      } catch (error) {
        console.warn('[AutoFastForward] check error:', error);
      } finally {
        this.autoFastForwardPending = false;
        // Short cooldown; state polling / events will update phase/turn.
        this.time.delayedCall(250, () => {
          this.actionInProgress = false;
          this.refreshTableState();
        });
      }
    });
  }

  private buildBoardKey(cards: string[] | undefined): string {
    return (cards || []).slice(0, 5).join('|');
  }

  private async fetchBestHandForBoard(boardKey: string): Promise<void> {
    if (this.bestHandFetchInProgress || !pokerClient.tableId) {
      return;
    }
    if (this.lastBestHandFetchBoardKey === boardKey) {
      return;
    }

    this.bestHandFetchInProgress = true;
    this.lastBestHandFetchBoardKey = boardKey;

    try {
      const fullState = await pokerClient.getTableState(pokerClient.tableId);
      if (!fullState) {
        return;
      }

      const fetchedBest = (fullState as any).myBestHandCards as string[] | undefined;
      const fetchedBoardKey = this.buildBoardKey(fullState.communityCards);

      if (fetchedBest && fetchedBest.length >= 5) {
        this.cachedBestHandCards = [...fetchedBest];
        this.cachedBestHandBoardKey = fetchedBoardKey;
        this.queueTableStateUpdate(fullState);
        return;
      }

      // Still update UI if the board advanced while fetching
      if (fetchedBoardKey !== boardKey) {
        this.queueTableStateUpdate(fullState);
      }
    } catch (error) {
      console.warn('[Fetch] Failed to fetch best hand state:', error);
    } finally {
      this.bestHandFetchInProgress = false;
    }
  }

  private highlightBestHand(state: PokerTableState): void {
    const boardCount = state.communityCards?.length || 0;
    const boardKey = this.buildBoardKey(state.communityCards);
    if (boardCount < 3) {
      this.cachedBestHandCards = [];
      this.cachedBestHandBoardKey = '';
      this.lastBestHandFetchBoardKey = '';
      this.clearAllCardGlows();
      return;
    }

    const localPlayer = state.players.find(p => p.userId === this.localPlayerId);
    if (localPlayer?.isFolded || localPlayer?.isSittingOut) {
      this.clearAllCardGlows();
      return;
    }

    const bestFromState = (state as any).myBestHandCards as string[] | undefined;
    let bestHand = bestFromState && bestFromState.length >= 5 ? [...bestFromState] : null;

    if (bestHand) {
      this.cachedBestHandCards = [...bestHand];
      this.cachedBestHandBoardKey = boardKey;
      this.lastBestHandFetchBoardKey = '';
    } else if (this.cachedBestHandBoardKey === boardKey && this.cachedBestHandCards.length >= 5) {
      bestHand = [...this.cachedBestHandCards];
    } else {
      // Fetch full state once for this board if best hand is missing
      this.fetchBestHandForBoard(boardKey);
    }

    if (!bestHand || bestHand.length < 5) {
      // Don't clear glows while waiting for data to prevent flickering
      // this.clearAllCardGlows(); 
      return;
    }

    const remaining = new Map<string, number>();
    bestHand.forEach(card => {
      const normalized = this.normalizeCardCode(card);
      if (normalized) {
        remaining.set(normalized, (remaining.get(normalized) || 0) + 1);
      }
    });

    if (remaining.size === 0) {
      this.clearAllCardGlows();
      return;
    }

    // Start from clean slate each time
    // this.clearAllCardGlows(); // REMOVED to prevent flicker


    // Highlight board cards first (kolejność jak w bestHand, zliczając duplikaty)
    state.communityCards?.forEach((cardString, idx) => {
      if (idx >= this.communityCards.length) return;
      const normalized = this.normalizeCardCode(cardString || null);
      const count = normalized ? remaining.get(normalized) || 0 : 0;
      const shouldHighlight = count > 0;
      if (shouldHighlight && normalized) {
        remaining.set(normalized, count - 1);
      }
      this.setCardGlow(this.communityCards[idx], shouldHighlight);
    });

    // Highlight hole cards lokalnego gracza (visual position 0)
    const localSeat = this.playerSeats[0];
    const myCards = state.myCards || [];
    if (localSeat) {
      localSeat.cards.forEach((cardDisplay, idx) => {
        const normalized = this.normalizeCardCode(myCards[idx] || null);
        const count = normalized ? remaining.get(normalized) || 0 : 0;
        const shouldHighlight = count > 0;
        if (shouldHighlight && normalized) {
          remaining.set(normalized, count - 1);
        }
        this.setCardGlow(cardDisplay, shouldHighlight);
      });
    }
  }

  private getPhaseText(phase: string): string {
    const phases: { [key: string]: string } = {
      'waiting': 'Oczekiwanie na graczy',
      'preflop': 'Pre-Flop',
      'pre_flop': 'Pre-Flop',
      'flop': 'Flop',
      'turn': 'Turn',
      'river': 'River',
      'showdown': 'Showdown',
      'hand_complete': 'Ręka zakończona',
      'ended': 'Ręka zakończona',
    };
    return phases[phase] || phase;
  }

  private clearWinnerStatusTimer(): void {
    if (this.winnerStatusClearTimer) {
      this.winnerStatusClearTimer.destroy();
      this.winnerStatusClearTimer = null;
    }
  }

  private clearWinnerMessage(): void {
    if (this.winnerMessageContainer) {
      this.tweens.killTweensOf(this.winnerMessageContainer);
      this.winnerMessageContainer.destroy(true);
      this.winnerMessageContainer = null;
    }
    if (this.winnerStatusClearTimer) {
      this.winnerStatusClearTimer.destroy();
      this.winnerStatusClearTimer = null;
    }
    if (this.winnerStatusActive && !this.showdownWinnersOverlay) {
      this.statusText.setText('');
      this.winnerStatusActive = false;
    }
  }

  private clearWinnerAnnouncements(): void {
    const hadOverlay = !!this.showdownWinnersOverlay || this.showdownWinnersQueue.length > 0;
    const hadMessage = !!this.winnerMessageContainer || !!this.winnerStatusClearTimer;
    this.clearWinnerMessage();
    if (this.showdownWinnersOverlay) {
      this.showdownWinnersOverlay.destroy(true);
      this.showdownWinnersOverlay = null;
    }
    if (this.showdownWinnersQueue.length > 0) {
      this.showdownWinnersQueue = [];
    }
    if ((hadOverlay || hadMessage || this.winnerStatusActive) && !this.showdownWinnersOverlay) {
      this.statusText.setText('');
      this.winnerStatusActive = false;
    }
  }

  private clearBustedMessageQueue(): void {
    if (this.bustedMessageText) {
      this.tweens.killTweensOf(this.bustedMessageText);
      this.bustedMessageText.destroy();
      this.bustedMessageText = null;
    }
    this.bustedMessageQueue = [];
  }

  private getBustedMessageDurationMs(totalCount: number): number {
    const elapsedSinceShowdown = this.lastShowdownAtMs !== null
      ? Math.max(0, this.time.now - this.lastShowdownAtMs)
      : null;
    const baseWindowMs = elapsedSinceShowdown !== null
      ? Math.max(1800, 5000 - elapsedSinceShowdown)
      : 4500;
    const perMessageMs = Math.floor(baseWindowMs / Math.max(1, totalCount));
    return Math.max(1000, perMessageMs);
  }

  private showNextBustedMessage(): void {
    if (this.bustedMessageQueue.length === 0) return;

    if (this.bustedMessageText) {
      this.tweens.killTweensOf(this.bustedMessageText);
      this.bustedMessageText.destroy();
      this.bustedMessageText = null;
    }

    const totalCount = this.bustedMessageQueue.length;
    const message = this.bustedMessageQueue.shift() as string;
    const displayMs = this.getBustedMessageDurationMs(totalCount);
    const fadeMs = Math.min(1200, Math.floor(displayMs * 0.4));
    const holdMs = Math.max(0, displayMs - fadeMs);

    const bustedText = this.add.text(960, 140, message, {
      fontSize: '26px',
      color: '#ff6b6b',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      padding: { x: 18, y: 10 },
      align: 'center',
    }).setOrigin(0.5);
    bustedText.setDepth(10020);
    this.bustedMessageText = bustedText;

    this.tweens.add({
      targets: bustedText,
      alpha: 0,
      y: 110,
      duration: fadeMs,
      delay: holdMs,
      onComplete: () => {
        bustedText.destroy();
        if (this.bustedMessageText === bustedText) {
          this.bustedMessageText = null;
        }
        this.showNextBustedMessage();
      }
    });
  }

  private enqueueBustedMessage(message: string): void {
    this.bustedMessageQueue.push(message);
    if (!this.bustedMessageText) {
      this.showNextBustedMessage();
    }
  }

  private showWinnerMessage(winnerName: string, amount: number, handDescription: string): void {
    // Przetłumacz nazwę układu na polski
    const translatedHand = handDescription ? this.translateHandName(handDescription) : '';
    
    // Wyświetl duży komunikat o zwycięzcy na środku ekranu
    this.clearWinnerMessage();
    const messageContainer = this.add.container(960, 380);
    const winnerText = this.add.text(0, 0, `🏆 ${winnerName} wygrywa ${amount} 💰`, {
      fontSize: '42px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: { x: 20, y: 15 },
      align: 'center',
    }).setOrigin(0.5);
    messageContainer.add(winnerText);
    
    // Wyświetl układ pokerowy poniżej, jeśli istnieje
    let handText: Phaser.GameObjects.Text | null = null;
    if (translatedHand) {
      handText = this.add.text(0, 75, `Zwycięski układ: ${translatedHand}`, {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: 'Rajdhani, Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: { x: 15, y: 10 },
        align: 'center',
      }).setOrigin(0.5);
      messageContainer.add(handText);
    }
    this.winnerMessageContainer = messageContainer;
    
    // Animacja - powiększ i znikaj
    this.tweens.add({
      targets: messageContainer,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 300,
      yoyo: true,
      repeat: 1,
      onComplete: () => {
        this.tweens.add({
          targets: messageContainer,
          alpha: 0,
          y: '-=50',
          duration: 2000,
          delay: 2000,
          onComplete: () => {
            messageContainer.destroy();
            if (this.winnerMessageContainer === messageContainer) {
              this.winnerMessageContainer = null;
            }
          }
        });
      }
    });
    
    // Również zaktualizuj status text
    const statusHandText = translatedHand ? ` (${translatedHand})` : '';
    this.statusText.setText(`${winnerName} wygrywa ${amount} 💰${statusHandText}!`).setColor('#d4af37');
    this.winnerStatusActive = true;
    this.clearWinnerStatusTimer();
    this.winnerStatusClearTimer = this.time.delayedCall(5000, () => {
      if (!this.winnerStatusActive) return;
      this.statusText.setText('');
      this.winnerStatusActive = false;
      this.winnerStatusClearTimer = null;
    });
  }

  private showWinnerMessageManual(winnerName: string, amount: number, handDescription: string): void {
    this.showShowdownWinnersOverlay([
      { winnerName, amount, handDescription },
    ]);
  }

  private showShowdownWinnersOverlay(entries: Array<{ winnerName: string; amount: number; handDescription: string }>): void {
    if (this.showdownWinnersOverlay) {
      this.showdownWinnersOverlay.destroy(true);
      this.showdownWinnersOverlay = null;
    }

    const overlay = this.add.container(0, 0);

    const translatedHands = entries
      .map((e) => (e?.handDescription ? this.translateHandName(e.handDescription) : ''))
      .filter((t) => !!t);
    const uniqueHands = Array.from(new Set(translatedHands));
    const singleHand = uniqueHands.length === 1 ? uniqueHands[0] : '';

    const titleText = this.add.text(960, 305, '🏆 Wyniki rozdania', {
      fontSize: '38px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: { x: 20, y: 12 },
      align: 'center',
    }).setOrigin(0.5);
    overlay.add(titleText);

    const baseY = 385;
    const lineGap = 52;

    entries
      .filter((e) => (e?.amount ?? 0) > 0)
      .forEach((entry, index) => {
        const translated = entry?.handDescription ? this.translateHandName(entry.handDescription) : '';
        const handSuffix = !singleHand && translated ? ` (${translated})` : '';
        const formattedAmount = this.formatPolishNumber(entry.amount);

        const line = this.add.text(960, baseY + index * lineGap, `🏆 ${entry.winnerName}: ${formattedAmount} 💰${handSuffix}`, {
          fontSize: '34px',
          color: '#ffffff',
          fontFamily: 'Rajdhani, Arial, sans-serif',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 3,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          padding: { x: 18, y: 10 },
          align: 'center',
        }).setOrigin(0.5);
        overlay.add(line);
      });

    if (singleHand) {
      const y = baseY + Math.max(0, entries.length - 1) * lineGap + 70;
      const handText = this.add.text(960, y, `Zwycięski układ: ${singleHand}`, {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: 'Rajdhani, Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: { x: 15, y: 10 },
        align: 'center',
      }).setOrigin(0.5);
      overlay.add(handText);
    }

    overlay.setDepth(9999);
    this.showdownWinnersOverlay = overlay;

    const statusSummary = entries
      .filter((e) => (e?.amount ?? 0) > 0)
      .map((e) => `${e.winnerName}: ${this.formatPolishNumber(e.amount)} 💰`)
      .join(' | ');
    this.statusText.setText(statusSummary).setColor('#d4af37');
    this.winnerStatusActive = true;
    this.clearWinnerStatusTimer();
  }

  private setShowdownWinnersQueue(entries: Array<{ winnerName: string; amount: number; handDescription: string }>): void {
    this.showdownWinnersQueue = entries.filter(e => (e?.amount ?? 0) > 0);
    if (this.showdownWinnersQueue.length === 0) {
      if (this.showdownWinnersOverlay) {
        this.showdownWinnersOverlay.destroy(true);
        this.showdownWinnersOverlay = null;
      }
      return;
    }

    // Show all winners at once (split pots can have multiple winners)
    this.showShowdownWinnersOverlay(this.showdownWinnersQueue);
  }

  private showNextShowdownWinner(): void {
    if (!this.showdownWinnersQueue || this.showdownWinnersQueue.length === 0) return;

    // Previous behavior cycled entries one-by-one. Now Enter dismisses the overlay.
    this.showdownWinnersQueue = [];
    if (this.showdownWinnersOverlay) {
      this.showdownWinnersOverlay.destroy(true);
      this.showdownWinnersOverlay = null;
    }
    this.statusText.setText('');
    this.winnerStatusActive = false;
    this.clearWinnerStatusTimer();
  }

  private onEnterKey(): void {
    const activeEl = (typeof document !== 'undefined' ? document.activeElement : null) as HTMLElement | null;
    const activeTag = activeEl?.tagName?.toLowerCase();
    if (activeTag === 'input' || activeTag === 'textarea') return;

    if (this.showdownWinnersQueue.length > 0) {
      this.showNextShowdownWinner();
    }
  }

  private handleShowdown(event: any): void {
    this.lastShowdownAtMs = this.time.now;
    
    // Update table state if included in event
    if (event.tableState) {
      this.tableState = event.tableState;
      this.updateTableUI(event.tableState);
    }
    
    // Event zawiera: payouts (array of objects: {playerId, amount, handInfo}), communityCards, playerCards
    const payouts = event.payouts || []; // [{playerId, amount, handInfo}, ...]
    const playerCards = event.playerCards || []; // [{userId, cards}, ...]
    
    // Znajdź zwycięzców (wszystkich z payout > 0) i posortuj od najwyższej wygranej
    const winners = payouts
      .filter((p: any) => (p?.amount ?? 0) > 0)
      .sort((a: any, b: any) => (b?.amount ?? 0) - (a?.amount ?? 0));
    
    // Wyświetl karty wszystkich graczy
    this.statusText.setText('SHOWDOWN!').setColor('#d4af37');
    
    // Update opponent cards to show them face-up during showdown
    this.showShowdownCards(playerCards);

    // Zachowaj odkryte karty do czasu kolejnego rozdania
    playerCards.forEach((pc: any) => {
      if (pc?.cards && pc.cards.length > 0) {
        this.revealedCards.set(pc.userId, pc.cards);
      }
    });
    
    // Wyświetl wyniki (kolejka, Enter = następny)
    if (winners.length > 0) {
      const queue = winners.map((payout: any) => {
        const { playerId, amount, handInfo } = payout;
        const player = this.tableState?.players?.find(p => p.userId === playerId);
        const playerName = player?.username || 'Gracz';
        const playerCardsData = playerCards.find((pc: any) => pc.userId === playerId);
        const cardsStr = playerCardsData?.cards?.join(' ') || '';
        const handDescription = handInfo?.handName || handInfo?.handDescription || '';

        return { winnerName: playerName, amount, handDescription };
      });

      this.setShowdownWinnersQueue(queue);
    } else {
      this.setShowdownWinnersQueue([]);
    }
  }

  /**
   * Show all player cards during showdown
   */
  private showShowdownCards(playerCards: Array<{userId: string, cards: string[]}>): void {
    if (!this.tableState) return;
    
    const localServerPosition = this.tableState.players.find(p => p.userId === this.localPlayerId)?.seatNumber ?? 0;
    
    for (const pc of playerCards) {
      const player = this.tableState.players.find(p => p.userId === pc.userId);
      if (!player) continue;
      
      const playerSeat = player.seatNumber ?? 0;
      const visualPosition = this.serverToVisualPosition(playerSeat, localServerPosition, this.tableState.maxPlayers);
      
      if (visualPosition >= 0 && visualPosition < this.playerSeats.length) {
        const seat = this.playerSeats[visualPosition];
        // Show cards face-up
        pc.cards.forEach((cardString, i) => {
          if (i < seat.cards.length) {
            this.updateCardDisplay(seat.cards[i], cardString, false);
          }
        });
      }
    }
  }

  private updateCommunityCards(cards: string[], animate: boolean): void {
    const previousCount = this.lastCommunityCards.length;
    const newCount = Math.min(cards.length, 5);
    const shouldShowBacks = newCount >= 3 && newCount < 5;
    const previousBackCount = previousCount >= 3 ? 5 - previousCount : 0;
    
    for (let i = 0; i < 5; i++) {
      const cardString = cards[i] || null;
      const display = this.communityCards[i];

      if (cardString) {
        if (!animate || i < previousCount) {
          this.updateCardDisplay(display, cardString, false);
        } else {
          const targetX = display.container.x;
          const targetY = display.container.y;
          this.updateCardDisplay(display, cardString, false);
          this.playDealTween(display, targetX, targetY, (i - previousCount) * 120, this.communityCardsContainer);
        }
        continue;
      }

      if (shouldShowBacks && i >= newCount && i < 5) {
        const alreadyHadBack = i < previousCount + previousBackCount;
        if (!animate || alreadyHadBack) {
          this.updateCardDisplay(display, null, true);
        } else {
          const targetX = display.container.x;
          const targetY = display.container.y;
          this.updateCardDisplay(display, null, true);
          this.playDealTween(display, targetX, targetY, (i - previousCount) * 120, this.communityCardsContainer);
        }
      } else {
        this.updateCardDisplay(display, null, false);
      }
    }

    this.lastCommunityCards = cards.slice(0, 5);
  }

  private playDealTween(card: CardDisplay, targetX: number, targetY: number, delay: number = 0, parent?: Phaser.GameObjects.Container): void {
    const offsetX = parent?.x || 0;
    const offsetY = parent?.y || 0;
    const startX = this.deckPosition.x - offsetX;
    const startY = this.deckPosition.y - offsetY;

    // If multiple state updates arrive quickly (polling), prevent overlapping tweens
    // from leaving a card in a half-transformed state.
    this.tweens.killTweensOf(card.container);

    card.container.setVisible(true);
    card.container.setPosition(startX, startY);
    card.container.setScale(0.45);
    card.container.setAlpha(0);
    card.container.setAngle(Phaser.Math.Between(-8, 8));
    this.tweens.add({
      targets: card.container,
      x: targetX,
      y: targetY,
      alpha: 1,
      scale: 1,
      angle: 0,
      duration: 220,
      delay,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        card.container.setPosition(targetX, targetY);
        this.resetCardContainerTransform(card.container);
      },
    });

    // SFX: card dealing/placing
    this.time.delayedCall(delay, () => {
      this.playSfxByPrefixes(['card-slide', 'card-place'], { volume: 0.12, rateLimitKey: 'deal', minIntervalMs: 30 });
    });
  }

  private pickRandomCardBack(): string {
    if (this.availableCardBackKeys.length === 0) {
      return CARD_BACK_KEYS_BY_DECK.classic[0] || '';
    }
    const idx = Phaser.Math.Between(0, this.availableCardBackKeys.length - 1);
    return this.availableCardBackKeys[idx];
  }

  private updatePlayerSeats(state: PokerTableState, animate: boolean): void {
    const activeUserIds = new Set(state.players.map(p => p.userId));
    for (const userId of Array.from(this.playerCardCounts.keys())) {
      if (!activeUserIds.has(userId)) {
        this.playerCardCounts.delete(userId);
      }
    }
    for (const userId of Array.from(this.lastKnownServerSeatByUserId.keys())) {
      if (!activeUserIds.has(userId)) {
        this.lastKnownServerSeatByUserId.delete(userId);
      }
    }
    for (const userId of Array.from(this.lastBetByUserId.keys())) {
      if (!activeUserIds.has(userId)) {
        this.lastBetByUserId.delete(userId);
        this.lastFoldStateByUserId.delete(userId);
        this.lastAllInStateByUserId.delete(userId);
      }
    }
    
    // Find local player's server position
    const localPlayer = state.players.find(p => p.userId === this.localPlayerId);
    const localServerPosition = localPlayer
      ? this.getStableServerSeat(localPlayer, 0, state.maxPlayers)
      : (this.lastKnownLocalServerSeat ?? 0);
    
    // Build a map of visual positions occupied by players
    const occupiedVisualPositions = new Set<number>();
    for (let idx = 0; idx < state.players.length; idx++) {
      const player = state.players[idx];
      const playerSeat = this.getStableServerSeat(player, idx, state.maxPlayers);
      const visualPosition = this.serverToVisualPosition(playerSeat, localServerPosition, state.maxPlayers);
      occupiedVisualPositions.add(visualPosition);
    }
    
    // Only reset seats that are NOT occupied by any player (empty seats)
    for (let i = 0; i < this.playerSeats.length; i++) {
      if (!occupiedVisualPositions.has(i)) {
        this.resetSeat(this.playerSeats[i]);
      } else {
        // For occupied seats, only reset highlight ring (it will be set again if needed)
        this.playerSeats[i].highlightRing.setVisible(false);
      }
    }
    
    const phase = state.currentPhase || state.phase || 'waiting';
    const isActivePhase = phase !== 'waiting' && phase !== 'showdown' && phase !== 'hand_complete' && phase !== 'ended';

    // Get active player seat for comparison
    const activePlayerSeat = state.activePlayerSeat ?? state.currentPlayerPosition ?? -1;
    const activePlayerChanged = activePlayerSeat !== this.lastActivePlayerSeat;

    // During showdown / hand end / waiting phases we don't need the timer bar.
    if (!isActivePhase) {
      this.stopTurnTimerAndHide();
      this.lastActivePlayerSeat = -1;
    } else if (activePlayerChanged) {
      // Only stop timer if active player changed
      this.stopTurnTimerAndHide();
      this.lastActivePlayerSeat = activePlayerSeat;
    }
    
    // Update seats with players - map server positions to visual positions
    // Local player always at visual position 0 (bottom)
    for (let idx = 0; idx < state.players.length; idx++) {
      const player = state.players[idx];
      const playerSeat = this.getStableServerSeat(player, idx, state.maxPlayers);
      const visualPosition = this.serverToVisualPosition(playerSeat, localServerPosition, state.maxPlayers);
      if (visualPosition >= 0 && visualPosition < this.playerSeats.length) {
        this.updateSeat(this.playerSeats[visualPosition], player, state, animate);
      }
    }
    
    // Highlight current player (only in active phases)
    if (isActivePhase && activePlayerSeat >= 0) {
      const currentVisualPos = this.serverToVisualPosition(activePlayerSeat, localServerPosition, state.maxPlayers);
      if (currentVisualPos >= 0 && currentVisualPos < this.playerSeats.length) {
        const currentSeat = this.playerSeats[currentVisualPos];
        currentSeat.highlightRing.setVisible(true);
        
        // Only start timer animation if active player changed
        if (activePlayerChanged) {
          this.startTurnTimer(currentVisualPos);
          // Reset action in progress when turn changes
          this.actionInProgress = false;
          // Reset slider touched flag when turn changes
          this.raiseSliderTouched = false;
        }
        
        // Jeśli timer jest przy nas (visualPos 0 = lokalny gracz), pokaż przyciski
        if (currentVisualPos === 0 && localPlayer && !localPlayer.isFolded && !localPlayer.isAllIn && !localPlayer.isSittingOut && !this.actionInProgress) {
          this.setActionButtonsVisible(true);
          this.raiseSliderContainer.setVisible(true);
          
          // Configure raise slider
          const localChips = localPlayer.stack ?? localPlayer.chips ?? 0;
          const sliderBigBlind = state.bigBlind || 1;
          this.raiseStep = Math.max(1, Math.round(sliderBigBlind / 10));
          const desiredMinRaise = state.currentBet * 2 || sliderBigBlind;
          this.maxRaise = Math.max(localChips, 0);
          this.minRaise = Math.min(desiredMinRaise, this.maxRaise || desiredMinRaise);
          this.updateRaiseInputStep();
          // Only reset to minRaise if user hasn't touched slider yet or turn changed
          if (!this.raiseSliderTouched) {
            this.setRaiseAmount(this.minRaise);
          } else {
            this.setRaiseAmount(this.currentRaiseAmount);
          }
        }
      }
    }
  }

  private stopTurnTimerAndHide(): void {
    if (this.turnTimer) {
      this.turnTimer.destroy();
      this.turnTimer = null;
    }
    if (this.currentTimerSeat >= 0 && this.currentTimerSeat < this.playerSeats.length) {
      const seat = this.playerSeats[this.currentTimerSeat];
      seat.timerBar.setVisible(false);
      seat.timerBarBg.setVisible(false);
    }
    this.currentTimerSeat = -1;
  }

  /**
   * Convert server seat position to visual position
   * Visual position 0 is always the local player (bottom of screen)
   * Other positions go clockwise from player's left
   */
  private serverToVisualPosition(serverPosition: number, localServerPosition: number, maxPlayers: number): number {
    // Calculate offset so local player is at position 0
    const offset = (serverPosition - localServerPosition + maxPlayers) % maxPlayers;
    return offset;
  }

  private getLocalCardY(): number {
    const avatarTopY = AVATAR_CENTER_Y - AVATAR_RADIUS;
    return avatarTopY - LOCAL_CARD_TOP_OFFSET;
  }

  private getOpponentCardY(cardHeight: number): number {
    const avatarBottomY = AVATAR_CENTER_Y + AVATAR_RADIUS;
    return avatarBottomY + AVATAR_CARD_GAP - (cardHeight / 2);
  }

  private getFallbackAvatarKey(seed: string): string {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }
    const index = Math.abs(hash) % AVATAR_PRESET_KEYS.length;
    return AVATAR_PRESET_KEYS[index] || AVATAR_PRESET_KEYS[0];
  }


  private buildAvatarTextureKey(value: string, prefix: string): string {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = (hash * 31 + value.charCodeAt(i)) | 0;
    }
    const safeHash = Math.abs(hash);
    return `${prefix}_${safeHash}`;
  }

  private queueRemoteAvatarTexture(url: string, key: string): void {
    if (this.pendingAvatarLoads.has(url) || this.textures.exists(key)) {
      return;
    }

    this.pendingAvatarLoads.set(url, key);
    
    // Ładuj obrazek z URL i przetwórz do okrągłego kształtu
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Utwórz canvas do przetworzenia obrazka na okrągły
      const canvas = document.createElement('canvas');
      canvas.width = AVATAR_DIAMETER;
      canvas.height = AVATAR_DIAMETER;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        // Wyczyść canvas
        ctx.clearRect(0, 0, AVATAR_DIAMETER, AVATAR_DIAMETER);
        
        // Utwórz okrągłą ścieżkę obcinania
        ctx.beginPath();
        ctx.arc(AVATAR_RADIUS, AVATAR_RADIUS, AVATAR_RADIUS, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        
        // Narysuj obrazek w okręgu
        ctx.drawImage(img, 0, 0, AVATAR_DIAMETER, AVATAR_DIAMETER);
        
        // Konwertuj canvas do base64 i załaduj do Phaser
        const circularImageUrl = canvas.toDataURL();
        this.textures.addBase64(key, circularImageUrl);
        
        this.pendingAvatarLoads.delete(url);
        if (this.tableState) {
          this.updatePlayerSeats(this.tableState, false);
        }
      }
    };
    
    img.onerror = () => {
      console.error('Failed to load avatar:', url);
      this.pendingAvatarLoads.delete(url);
    };
    
    img.src = url;
  }

  private resolveAvatarTextureKey(player: PlayerState): string {
    const avatarValue = (player.avatar || '').trim();
    if (avatarValue) {
      if (avatarValue.startsWith('http://') || avatarValue.startsWith('https://')) {
        const remoteKey = this.buildAvatarTextureKey(avatarValue, 'avatar_remote');
        if (!this.textures.exists(remoteKey)) {
          this.queueRemoteAvatarTexture(avatarValue, remoteKey);
          return this.getFallbackAvatarKey(player.userId);
        }
        return remoteKey;
      }
      if (this.textures.exists(avatarValue)) {
        return avatarValue;
      }
    }
    return this.getFallbackAvatarKey(player.userId);
  }

  private updateChipsBackground(seat: PlayerSeatUI): void {
    const textValue = seat.chipsText.text;
    if (!textValue) {
      seat.chipsBg.clear();
      seat.chipsBg.setVisible(false);
      return;
    }

    seat.chipsText.updateText();
    const width = Math.max(1, seat.chipsText.width) + (CHIPS_BG_PADDING_X * 2);
    const height = Math.max(1, seat.chipsText.height) + (CHIPS_BG_PADDING_Y * 2);

    seat.chipsBg.clear();
    seat.chipsBg.fillStyle(CHIPS_BG_COLOR, CHIPS_BG_ALPHA);
    seat.chipsBg.fillRoundedRect(-width / 2, -height / 2, width, height, CHIPS_BG_RADIUS);
    seat.chipsBg.setPosition(seat.chipsText.x, seat.chipsText.y);
    seat.chipsBg.setVisible(true);
  }

  private resetSeat(seat: PlayerSeatUI): void {
    seat.nameText.setText('Puste').setColor('#888888');
    seat.chipsText.setText('');
    this.updateChipsBackground(seat);
    seat.chipsIcon.setVisible(false);
    seat.statusText.setText('');
    seat.avatar.setVisible(false); // Ukryj awatar kiedy miejsce puste
    seat.background.setVisible(false);
    seat.dealerChip.setVisible(false);
    seat.betText.setText('');
    seat.betChips.forEach(chip => chip.setVisible(false));
    seat.highlightRing.setVisible(false);
    seat.cards.forEach(card => {
      this.tweens.killTweensOf(card.container);
      this.resetCardContainerTransform(card.container);
      this.updateCardDisplay(card, null, false);
      this.setCardGlow(card, false);
    });
  }

  private updateSeat(seat: PlayerSeatUI, player: PlayerState, state: PokerTableState, animate: boolean): void {
    // Name and chips (use stack from API)
    const chips = player.stack ?? player.chips ?? 0;
    const isLocalPlayer = player.userId === this.localPlayerId;
    seat.nameText.setText(player.username).setColor('#ffffff');
    seat.chipsText.setText(`${chips}`);
    this.updateChipsBackground(seat);
    seat.chipsIcon.setVisible(false); // Ukryj żółty żeton
    const avatarKey = this.resolveAvatarTextureKey(player);
    if (seat.avatar.texture.key !== avatarKey) {
      seat.avatar.setTexture(avatarKey);
    }
    seat.avatar.setVisible(true); // Pokaż awatar kiedy gracz zasiada
    
    // Avatar color based on player state - już nie używamy koloru bo to obrazek
    // ale możemy zmienić opacity/tint
    if (player.isFolded) {
      seat.avatar.setTint(0x666666); // Przyciemniony
      seat.statusText.setText('FOLD').setColor('#888888');
    } else if (player.isAllIn) {
      seat.avatar.setTint(0xffffff); // Normalny
      seat.statusText.setText('ALL-IN').setColor('#d4af37');
    } else if (player.isSittingOut) {
      seat.avatar.setTint(0x888888); // Szary
      seat.statusText.setText('AWAY').setColor('#888888');
    } else {
      seat.avatar.clearTint(); // Normalny kolor
      seat.statusText.setText('');
    }
    
    // Highlight local player
    if (isLocalPlayer) {
      seat.background.setStrokeStyle(2, 0x6bcf7f);
    }
    
    // Dealer chip
    seat.dealerChip.setVisible(player.isDealer);
    
    // Bet amount (use currentBet from API)
    const playerBet = player.currentBet ?? player.bet ?? 0;

    // SFX: detect bet increase (call/raise/all-in posts chips)
    const previousBet = this.lastBetByUserId.get(player.userId) ?? 0;
    if (animate && playerBet > previousBet) {
      this.playSfxByPrefixes(['chip-lay', 'chips-handle'], { volume: 0.14, rateLimitKey: `bet_${player.userId}`, minIntervalMs: 150 });
    }
    this.lastBetByUserId.set(player.userId, playerBet);

    // SFX: detect fold/all-in transitions for other players (and for polling updates)
    const prevFold = this.lastFoldStateByUserId.get(player.userId) ?? false;
    if (animate && player.isFolded && !prevFold) {
      this.playSfxByPrefixes(['card-shove'], { volume: 0.12, rateLimitKey: `fold_${player.userId}`, minIntervalMs: 450 });
    }
    this.lastFoldStateByUserId.set(player.userId, !!player.isFolded);

    const prevAllIn = this.lastAllInStateByUserId.get(player.userId) ?? false;
    if (animate && player.isAllIn && !prevAllIn) {
      this.playSfxByPrefixes(['chips-stack', 'chips-handle'], { volume: 0.18, rateLimitKey: `allin_${player.userId}`, minIntervalMs: 700 });
    }
    this.lastAllInStateByUserId.set(player.userId, !!player.isAllIn);

    if (playerBet > 0) {
      seat.betText.setText(playerBet.toString());
      this.updateBetChips(seat, playerBet);
    } else {
      seat.betText.setText('');
      this.updateBetChips(seat, 0);
    }
    
    // Wyświetl karty gracza
    const phase = state.currentPhase || state.phase || 'waiting';
    const useShowdownSizing = !isLocalPlayer && this.shouldEnlargeOpponentCards(state);
    const baseCardWidth = isLocalPlayer ? 94 : 50;
    const baseCardHeight = isLocalPlayer ? 132 : 70;
    const scale = useShowdownSizing ? 1.5 : 1;
    const cardWidth = Math.round(baseCardWidth * scale);
    const cardHeight = Math.round(baseCardHeight * scale);
    const cardY = isLocalPlayer ? this.getLocalCardY() : this.getOpponentCardY(cardHeight);
    this.layoutSeatCards(seat, cardWidth, cardHeight, cardY, useShowdownSizing);

    const isActivePhase = phase !== 'waiting' && phase !== 'showdown' && phase !== 'hand_complete' && phase !== 'ended';

    const cardViews = this.getPlayerCardViews(player, state);
    const previousCount = this.playerCardCounts.get(player.userId) || 0;
    const visibleCount = Math.min(cardViews.length, seat.cards.length);
    let nextCount = visibleCount;

    if (cardViews.length > 0) {
      // Only animate when cards are actually appearing (e.g., deal at start of hand).
      // Avoid re-dealing animations on routine polling updates to prevent flicker.
      const shouldAnimateDeal = animate && isActivePhase && previousCount === 0 && visibleCount > 0;
      if (shouldAnimateDeal) {
        this.showPlayerCardsWithAnimation(seat, cardViews, previousCount);
      } else {
        this.showPlayerCardsImmediately(seat, cardViews);
      }
    } else {
      // If backend briefly omits cards during an active hand, keep the last rendered cards
      // instead of hiding them and re-animating on the next poll.
      const shouldKeepPrevious = isActivePhase && previousCount > 0 && !player.isFolded && !player.isSittingOut;
      if (shouldKeepPrevious) {
        nextCount = previousCount;
      } else {
        seat.cards.forEach(card => {
          this.tweens.killTweensOf(card.container);
          this.resetCardContainerTransform(card.container);
          card.container.setVisible(false);
        });
      }
    }

    this.playerCardCounts.set(player.userId, nextCount);
  }

  private getPlayerCardViews(player: PlayerState, state: PokerTableState): CardView[] {
    const phase = state.currentPhase || state.phase || 'waiting';
    const isLocalPlayer = player.userId === this.localPlayerId;
    const isShowdown = phase === 'showdown';
    const isActivePhase = phase !== 'waiting' && phase !== 'showdown' && phase !== 'hand_complete' && phase !== 'ended';

    if (isLocalPlayer && state.myCards && state.myCards.length > 0) {
      return state.myCards.slice(0, 2).map(card => ({ card, faceDown: false }));
    }

    if (player.cards && player.cards.length > 0) {
      const hideValues = !isLocalPlayer && !this.revealedCards.has(player.userId) && (!isShowdown || player.isFolded);
      return player.cards.slice(0, 2).map(card => ({ card, faceDown: hideValues }));
    }

    if (this.revealedCards.has(player.userId)) {
      const revealed = this.revealedCards.get(player.userId) || [];
      return revealed.slice(0, 2).map(card => ({ card, faceDown: false }));
    }

    if (player.hasCards || (player.cardCount ?? 0) > 0) {
      const count = Math.min(player.cardCount || 2, 2);
      return Array.from({ length: count }, () => ({ card: 'hidden', faceDown: true }));
    }

    // Fallback: jeśli jesteśmy w aktywnej fazie i gracz nie spasował, pokaż 2 zakryte
    if (isActivePhase && !player.isFolded && !player.isSittingOut) {
      return Array.from({ length: 2 }, () => ({ card: 'hidden', faceDown: true }));
    }

    return [];
  }

  private showPlayerCardsImmediately(seat: PlayerSeatUI, cardViews: CardView[]): void {
    seat.cards.forEach((card, idx) => {
      const view = cardViews[idx];
      if (view) {
        this.updateCardDisplay(card, view.card, view.faceDown);
      } else {
        this.updateCardDisplay(card, null, false);
      }
    });
  }

  private showPlayerCardsWithAnimation(seat: PlayerSeatUI, cardViews: CardView[], previousCount: number): void {
    if (cardViews.length === 0) {
      seat.cards.forEach(card => {
        this.updateCardDisplay(card, null, false);
      });
      return;
    }

    cardViews.forEach((view, idx) => {
      if (idx >= seat.cards.length) return;
      const display = seat.cards[idx];
      const targetX = display.container.x;
      const targetY = display.container.y;
      if (idx < previousCount) {
        this.updateCardDisplay(display, view.card, view.faceDown);
        return;
      }
      const delay = Math.max(0, idx - previousCount) * 90;
      this.updateCardDisplay(display, view.card, view.faceDown);
      this.playDealTween(display, targetX, targetY, delay, seat.container);
    });

    for (let i = cardViews.length; i < seat.cards.length; i++) {
      this.updateCardDisplay(seat.cards[i], null, false);
    }
  }

  private startTurnTimer(seatIndex: number): void {
    const seat = this.playerSeats[seatIndex];
    this.currentTimerSeat = seatIndex;
    
    seat.timerBarBg.setVisible(true);
    seat.timerBar.setVisible(true);
    seat.timerBar.width = 120;
    
    const duration = 30000; // 30 seconds
    const startTime = Date.now();
    
    this.turnTimer = this.time.addEvent({
      delay: 50,
      callback: () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 1 - elapsed / duration);
        seat.timerBar.width = 120 * remaining;
        
        // Color change based on time remaining
        if (remaining < 0.25) {
          seat.timerBar.setFillStyle(0xff6b6b);
        } else if (remaining < 0.5) {
          seat.timerBar.setFillStyle(0xffd93d);
        } else {
          seat.timerBar.setFillStyle(0x6bcf7f);
        }
      },
      loop: true,
    });
  }

  private updateActionButtons(state: PokerTableState): void {
    // Find local player
    const localPlayer = state.players.find(p => p.userId === this.localPlayerId);
    
    // Get values from API (with fallbacks for compatibility)
    const activePlayerSeat = state.activePlayerSeat ?? state.currentPlayerPosition ?? -1;
    const localSeat = localPlayer?.seatNumber ?? localPlayer?.position ?? -1;
    const phase = state.currentPhase || state.phase || 'waiting';
    const localChips = localPlayer?.stack ?? localPlayer?.chips ?? 0;
    const localBet = localPlayer?.currentBet ?? localPlayer?.bet ?? 0;
    
    // Check if it's our turn - warunki sprawdzające turę gracza
    // hand_complete, showdown, ended, waiting są nieaktywne
    const isActivePhase = phase !== 'waiting' && phase !== 'showdown' && phase !== 'ended' && phase !== 'hand_complete';
    const isOurTurn = localPlayer && 
                      activePlayerSeat === localSeat &&
                      !localPlayer.isFolded &&
                      !localPlayer.isAllIn &&
                      !localPlayer.isSittingOut &&
                      isActivePhase;
    
    // ZAWSZE pokazuj przyciski jeśli jesteśmy aktywnym graczem w aktywnej fazie
    const showButtons = isOurTurn;
    
    this.setActionButtonsVisible(showButtons || false);
    
    if (showButtons && localPlayer) {
      // Configure raise slider
      const sliderBigBlind = state.bigBlind || 1;
      this.raiseStep = Math.max(1, Math.round(sliderBigBlind / 10));
      const desiredMinRaise = state.currentBet * 2 || sliderBigBlind;
      this.maxRaise = Math.max(localChips, 0);
      this.minRaise = Math.min(desiredMinRaise, this.maxRaise || desiredMinRaise);
      this.updateRaiseInputStep();
      // Only reset to minRaise if user hasn't touched slider yet
      if (!this.raiseSliderTouched) {
        this.setRaiseAmount(this.minRaise);
      } else {
        this.setRaiseAmount(this.currentRaiseAmount);
      }
      this.raiseSliderContainer.setVisible(true);
      
      // Dynamiczne przyciski Check/Call w zależności od sytuacji
      const callAmount = (state.currentBet || 0) - localBet;
      const callAmountToShow = Math.min(callAmount, localChips);
      const bigBlind = state.bigBlind || 2;
      
      // Prosta logika: jeśli callAmount <= 0, pokaż CHECK, w przeciwnym razie pokaż CALL
      // Serwer waliduje czy dana akcja jest dozwolona (np. czy można checkować)
      if (callAmount <= 0) {
        // Brak różnicy w betach - można CHECK
        this.checkBtn.setVisible(true);
        this.checkBtnText.setVisible(true);
        this.callBtn.setVisible(false);
        this.callBtnText.setVisible(false);
      } else {
        // Jest różnica w betach - trzeba CALL
        this.checkBtn.setVisible(false);
        this.checkBtnText.setVisible(false);
        this.callBtn.setVisible(true);
        this.callBtnText.setVisible(true);
        // Jeśli przeciwnik zrobił all-in większy niż nasz stack, UI powinno pokazać max jaki możemy dopłacić.
        this.callBtnText.setText(`CALL ${callAmountToShow}`);
      }
    } else {
      this.raiseSliderContainer.setVisible(false);
    }
  }

  /**
   * Check sitting state and show buy-in popup if needed
   */
  private checkSittingState(state: PokerTableState): void {
    if (this.manualBuyinOnly || (this.bustedOptionsPopupGroup && this.bustedOptionsPopupGroup.visible)) {
      return;
    }
    const localPlayer = state.players.find(p => p.userId === this.localPlayerId);
    const phase = state.currentPhase || state.phase || 'waiting';
    
    // If player already sat down with buy-in, just handle sitting state
    if (this.hasSatDown) {
      if (localPlayer) {
        this.isSeated = true;
        this.isSittingOut = localPlayer.isSittingOut;
        
        // Auto sit-in after buy-in confirmation if sitting out
        if (this.isSittingOut && !this.pendingSitIn) {
          if (this.forceSitOutDueToStack) {
            const minRequired = this.minimumStackToReturn || state.bigBlind || 0;
            this.statusText.setText(`Masz za mało żetonów (min ${minRequired} 💰). Opuść stół lub dokup buy-in i dołącz ponownie.`).setColor('#ffd93d');
            this.showForcedSitoutPrompt(minRequired);
            return;
          }

          const canSitIn = phase === 'waiting' || phase === 'ended';
          if (canSitIn) {
            this.autoSitIn();
          } else {
            this.statusText.setText('Oczekiwanie na koniec rozdania...').setColor('#ffd93d');
          }
        } else {
          this.hideForcedSitoutPrompt();
        }
        
        // Clear status when seated and playing
        if (!this.isSittingOut && this.statusText.text.includes('Oczekiwanie')) {
          this.statusText.setText('');
        }
      }
      return;
    }
    
    // Player hasn't chosen buy-in yet - show popup
    if (!this.buyinPopupShown && !this.buyinPopupGroup.visible) {
      // Show buy-in popup when table state is loaded
      this.showBuyinPopup();
    }
  }

  /**
   * Automatically sit in to the game
   */
  private async autoSitIn(): Promise<void> {
    if (this.pendingSitIn) return;
    
    this.pendingSitIn = true;
    this.statusText.setText('Dołączanie do gry...').setColor('#ffd93d');
    
    try {
      const result = await pokerClient.sitIn();
      
      if (result.success) {
        this.isSittingOut = false;
        this.statusText.setText('Dołączono do gry!').setColor('#6bcf7f');
        this.time.delayedCall(2000, () => {
          if (this.statusText.text === 'Dołączono do gry!') {
            this.statusText.setText('');
          }
        });
      } else {
        this.statusText.setText(`Błąd: ${result.error}`).setColor('#ff6b6b');
      }
    } catch (error) {
      console.error('Auto sit-in error:', error);
      this.statusText.setText('Błąd dołączania do gry').setColor('#ff6b6b');
    } finally {
      this.pendingSitIn = false;
    }
  }

  /**
   * Manually sit out from the game
   */
  private async sitOut(): Promise<void> {
    this.statusText.setText('Opuszczanie gry...').setColor('#ffd93d');
    
    try {
      const result = await pokerClient.sitOut();
      
      if (result.success) {
        this.isSittingOut = true;
        this.statusText.setText('Siedzisz poza grą').setColor('#888888');
      } else {
        this.statusText.setText(`Błąd: ${result.error}`).setColor('#ff6b6b');
      }
    } catch (error) {
      console.error('Sit out error:', error);
      this.statusText.setText('Błąd opuszczania gry').setColor('#ff6b6b');
    }
  }

  private showForcedSitoutPrompt(minRequired: number): void {
    this.hideForcedSitoutPrompt();

    const container = this.add.container(960, 200);

    const bg = this.add.rectangle(0, 0, 700, 180, 0x000000, 0.8)
      .setStrokeStyle(2, 0xffd93d)
      .setOrigin(0.5);

    const title = this.add.text(0, -60, 'Zbyt mało żetonów', {
      fontSize: '32px',
      color: '#ffd93d',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    const desc = this.add.text(0, -10, `Minimum do gry: ${minRequired} 💰.`, {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif'
    }).setOrigin(0.5);

    const leaveBtn = this.createPromptButton(-150, 60, 'Wyjdź i wypłać', '#ff6b6b', async () => {
      const result = await pokerClient.leaveTable();
      if (!result.success) {
        this.statusText.setText('Nie można teraz opuścić stołu. Spróbuj ponownie po zakończeniu rozdania.')
          .setColor('#ff6b6b').setVisible(true);
        return;
      }
      this.scene.start('MenuScene');
    });

    const rebuyBtn = this.createPromptButton(150, 60, 'Dołącz ponownie', '#6bcf7f', async () => {
      const result = await pokerClient.leaveTable();
      if (!result.success) {
        this.statusText.setText('Nie można teraz opuścić stołu. Spróbuj ponownie po zakończeniu rozdania.')
          .setColor('#ff6b6b').setVisible(true);
        return;
      }
      this.scene.start('MenuScene');
    });

    container.add([bg, title, desc, leaveBtn, rebuyBtn]);
    this.forcedSitoutPrompt = container;
  }

  private hideForcedSitoutPrompt(): void {
    if (this.forcedSitoutPrompt) {
      this.forcedSitoutPrompt.destroy(true);
      this.forcedSitoutPrompt = undefined;
    }
  }

  private createPromptButton(x: number, y: number, label: string, color: string, onClick: () => void): Phaser.GameObjects.Container {
    const buttonWidth = 240;
    const buttonHeight = 60;

    const container = this.add.container(x, y);

    const rect = this.add.rectangle(0, 0, buttonWidth, buttonHeight, Phaser.Display.Color.HexStringToColor(color).color)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', onClick)
      .on('pointerover', () => rect.setAlpha(0.9))
      .on('pointerout', () => rect.setAlpha(1));

    const text = this.add.text(0, 0, label, {
      fontSize: '22px',
      color: '#000000',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    container.add([rect, text]);
    return container;
  }

  private setActionButtonsVisible(visible: boolean): void {
    this.actionButtonsContainer.setVisible(visible);
  }

  private async onActionButton(action: string): Promise<void> {
    // Prevent action spam
    if (this.actionInProgress) {
      console.log('[GameScene] Action already in progress, ignoring');
      return;
    }
    
    // Validate we're still on a valid hand
    const currentHandNumber = this.parseHandNumber(this.tableState?.currentHandNumber);
    if (currentHandNumber === null) {
      console.warn('[GameScene] No valid hand number, cannot perform action');
      return;
    }
    
    this.actionInProgress = true;
    this.statusText.setText('Wykonywanie akcji...').setColor('#ffd93d');

    // SFX: immediate feedback for local player's action
    switch (action) {
      case 'fold':
        this.playSfxByPrefixes(['card-shove'], { volume: 0.14, rateLimitKey: 'action_fold', minIntervalMs: 200 });
        break;
      case 'check':
        this.playSfxByPrefixes(['chips-handle'], { volume: 0.10, rateLimitKey: 'action_check', minIntervalMs: 200 });
        break;
      case 'call':
        this.playSfxByPrefixes(['chip-lay', 'chips-handle'], { volume: 0.14, rateLimitKey: 'action_call', minIntervalMs: 200 });
        break;
      case 'raise':
        this.playSfxByPrefixes(['chip-lay', 'chips-stack'], { volume: 0.16, rateLimitKey: 'action_raise', minIntervalMs: 200 });
        break;
      case 'allin':
        this.playSfxByPrefixes(['chips-stack', 'chips-handle'], { volume: 0.18, rateLimitKey: 'action_allin', minIntervalMs: 450 });
        break;
    }
    
    // Hide buttons immediately to prevent double-click
    this.setActionButtonsVisible(false);
    this.raiseSliderContainer.setVisible(false);
    
    // Reset slider touched flag after action
    this.raiseSliderTouched = false;
    
    let result;
    
    switch (action) {
      case 'fold':
        result = await pokerClient.fold();
        break;
      case 'check':
        result = await pokerClient.check();
        break;
      case 'call':
        result = await pokerClient.call();
        break;
      case 'raise':
        result = await pokerClient.raise(this.currentRaiseAmount);
        break;
      case 'allin':
        result = await pokerClient.allIn();
        break;
    }
    
    if (result && !result.success) {
      this.statusText.setText(`Błąd: ${result.error}`).setColor('#ff6b6b');
      this.time.delayedCall(2000, () => this.statusText.setText(''));
      // On error, reset cooldown immediately
      this.actionInProgress = false;
      this.refreshTableState();
    } else {
      this.statusText.setText('');
      // Action was successful - refresh state immediately
      // actionInProgress will be reset when new state arrives
      this.refreshTableState();
      
      // Failsafe: reset lock after delay in case state update fails
      this.time.delayedCall(2000, () => {
        if (this.actionInProgress) {
          console.warn('[GameScene] Action lock timeout - forcing reset');
          this.actionInProgress = false;
        }
      });
      
      // Failsafe: reset lock after delay in case state update fails
      this.time.delayedCall(2000, () => {
        if (this.actionInProgress) {
          console.warn('[GameScene] Action lock timeout - forcing reset');
          this.actionInProgress = false;
        }
      });
    }
  }

  private createLeavePopup(): void {
    this.leavePopupGroup = this.add.container(0, 0);
    this.leavePopupGroup.setVisible(false);
    this.leavePopupGroup.setDepth(12000);
    
    // Dark overlay
    const overlay = this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.7);
    overlay.setInteractive(); // Block clicks behind popup
    
    // Popup background
    const popupBg = this.add.rectangle(960, 540, 550, 220, 0x0d1530);
    popupBg.setStrokeStyle(2, 0xd4af37);
    
    // Title
    const popupTitle = this.add.text(960, 470, 'OPUŚCIĆ STÓŁ?', {
      fontSize: '28px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    
    // Message
    const popupMessage = this.add.text(960, 520, 'Czy na pewno chcesz opuścić stół\ni wrócić do menu?', {
      fontSize: '18px',
      color: '#cccccc',
      fontFamily: 'Share Tech Mono, monospace',
      align: 'center',
    }).setOrigin(0.5);
    
    // Confirm button (TAK)
    const confirmBtn = this.add.image(850, 600, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.5);
    const confirmBtnText = this.add.text(850, 600, 'TAK', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Cancel button (NIE)
    const cancelBtn = this.add.image(1070, 600, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.5);
    const cancelBtnText = this.add.text(1070, 600, 'NIE', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Hover effects
    confirmBtn.on('pointerover', () => confirmBtn.setTint(0xcccccc));
    confirmBtn.on('pointerout', () => confirmBtn.clearTint());
    cancelBtn.on('pointerover', () => cancelBtn.setTint(0xcccccc));
    cancelBtn.on('pointerout', () => cancelBtn.clearTint());
    
    // Button actions
    confirmBtn.on('pointerdown', () => this.confirmLeaveTable());
    cancelBtn.on('pointerdown', () => this.hideLeavePopup());
    
    // ESC key to close popup
    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.leavePopupGroup.visible) {
        this.hideLeavePopup();
      }
    });
    
    this.leavePopupGroup.add([overlay, popupBg, popupTitle, popupMessage, confirmBtn, confirmBtnText, cancelBtn, cancelBtnText]);
  }

  private createBustedOptionsPopup(): void {
    this.bustedOptionsPopupGroup = this.add.container(0, 0);
    this.bustedOptionsPopupGroup.setVisible(false);
    this.bustedOptionsPopupGroup.setDepth(1100);

    const overlay = this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.72);
    overlay.setInteractive();

    const popupBg = this.add.rectangle(960, 540, 680, 260, 0x0d1530);
    popupBg.setStrokeStyle(2, 0xff6b6b);

    const popupTitle = this.add.text(960, 460, 'KONIEC ŻETONÓW', {
      fontSize: '30px',
      color: '#ff6b6b',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const popupMessage = this.add.text(960, 505, 'Wybierz co dalej:', {
      fontSize: '18px',
      color: '#cccccc',
      fontFamily: 'Share Tech Mono, monospace',
      align: 'center',
    }).setOrigin(0.5);

    const menuBtn = this.add.image(0, 600, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.45);
    const menuBtnText = this.add.text(0, 600, 'MENU', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const buyinBtn = this.add.image(0, 600, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.45);
    const buyinBtnText = this.add.text(0, 600, 'BUY IN', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const watchBtn = this.add.image(0, 600, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.45);
    const watchBtnText = this.add.text(0, 600, 'OGLĄDAJ', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    const buttonGap = 20;
    const buttonCenterX = 960;
    const buttonStep = menuBtn.displayWidth + buttonGap;
    const menuX = buttonCenterX - buttonStep;
    const buyinX = buttonCenterX;
    const watchX = buttonCenterX + buttonStep;

    menuBtn.setX(menuX);
    menuBtnText.setX(menuX);
    buyinBtn.setX(buyinX);
    buyinBtnText.setX(buyinX);
    watchBtn.setX(watchX);
    watchBtnText.setX(watchX);

    const applyHover = (button: Phaser.GameObjects.Image): void => {
      button.on('pointerover', () => button.setTint(0xcccccc));
      button.on('pointerout', () => button.clearTint());
    };

    applyHover(menuBtn);
    applyHover(buyinBtn);
    applyHover(watchBtn);

    menuBtn.on('pointerdown', () => this.handleBustedMenu());
    buyinBtn.on('pointerdown', () => this.handleBustedBuyin());
    watchBtn.on('pointerdown', () => this.handleBustedWatch());

    this.bustedOptionsPopupGroup.add([
      overlay,
      popupBg,
      popupTitle,
      popupMessage,
      menuBtn,
      menuBtnText,
      buyinBtn,
      buyinBtnText,
      watchBtn,
      watchBtnText,
    ]);
  }

  private showBustedOptionsPopup(): void {
    if (this.bustedOptionsPopupGroup.visible) return;
    this.clearWinnerAnnouncements();
    this.bustedOptionsPopupGroup.setVisible(true);
  }

  private hideBustedOptionsPopup(): void {
    this.bustedOptionsPopupGroup.setVisible(false);
  }

  private async handleBustedMenu(): Promise<void> {
    this.hideBustedOptionsPopup();
    this.manualBuyinOnly = false;
    this.statusText.setText('Wracam do menu...').setColor('#ffd93d');
    if (pokerClient.tableId) {
      const result = await pokerClient.leaveTable();
      if (!result.success) {
        this.statusText.setText('Nie można teraz opuścić stołu. Spróbuj ponownie po zakończeniu rozdania.')
          .setColor('#ff6b6b').setVisible(true);
        return;
      }
    }
    this.scene.start('MenuScene');
  }

  private handleBustedBuyin(): void {
    this.hideBustedOptionsPopup();
    this.manualBuyinOnly = false;
    this.buyinPopupShown = false;
    if (!this.tableState) {
      this.refreshTableState().then(() => this.showBuyinPopup());
      return;
    }
    this.showBuyinPopup();
  }

  private handleBustedWatch(): void {
    this.hideBustedOptionsPopup();
    this.statusText.setText('Oglądasz rozgrywkę.').setColor('#888888');
    this.time.delayedCall(2000, () => {
      if (this.statusText.text === 'Oglądasz rozgrywkę.') {
        this.statusText.setText('');
      }
    });
    this.refreshTableState();
  }

  private showLeaveConfirm(): void {
    this.leavePopupGroup.setVisible(true);
  }

  private hideLeavePopup(): void {
    this.leavePopupGroup.setVisible(false);
  }

  private async confirmLeaveTable(): Promise<void> {
    this.hideLeavePopup();
    this.statusText.setText('Opuszczanie stołu...').setColor('#ffd93d');
    
    // Leave table if joined; do not return to the menu when the server refuses.
    if (pokerClient.tableId) {
      const result = await pokerClient.leaveTable();
      if (!result.success) {
        this.statusText
          .setText(result.error || 'Nie można teraz opuścić stołu. Spróbuj ponownie po zakończeniu rozdania.')
          .setColor('#ff6b6b')
          .setVisible(true);
        return;
      }
    }
    
    // Return to menu scene
    this.scene.start('MenuScene');
  }

  /**
   * Create buy-in popup for selecting amount before sitting down
   */
  private createBuyinPopup(): void {
    this.buyinPopupGroup = this.add.container(0, 0);
    this.buyinPopupGroup.setVisible(false);
    this.buyinPopupGroup.setDepth(1000);
    
    // Dark overlay
    const overlay = this.add.rectangle(960, 540, 1920, 1080, 0x000000, 0.7);
    overlay.setInteractive(); // Block clicks behind popup
    
    // Popup background - bigger for slider
    const popupBg = this.add.rectangle(960, 540, 600, 320, 0x0d1530);
    popupBg.setStrokeStyle(2, 0xd4af37);
    
    // Title
    const popupTitle = this.add.text(960, 410, 'WYBIERZ BUY-IN', {
      fontSize: '28px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    
    // Subtitle with table info
    this.buyinSubtitleText = this.add.text(960, 450, '', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5);
    
    // Buy-in amount display
    this.buyinAmountText = this.add.text(960, 505, this.formatBuyinAmount(0), {
      fontSize: '42px',
      color: '#d4af37',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    
    // Slider track background
    const sliderTrackBg = this.add.rectangle(960, 570, 400, 20, 0x333333);
    sliderTrackBg.setStrokeStyle(1, 0x555555);
    
    // Slider track fill (visual progress)
    this.buyinSliderFill = this.add.rectangle(760, 570, 0, 16, 0xd4af37);
    this.buyinSliderFill.setOrigin(0, 0.5);
    
    // Slider handle
    this.buyinSliderHandle = this.add.circle(760, 570, 18, 0xd4af37);
    this.buyinSliderHandle.setStrokeStyle(3, 0xffffff);
    this.buyinSliderHandle.setInteractive({ useHandCursor: true, draggable: true });
    
    // Min/Max labels
    this.buyinMinLabel = this.add.text(760, 600, 'MIN', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Rajdhani, Arial, sans-serif',
    }).setOrigin(0.5);
    
    this.buyinMaxLabel = this.add.text(1160, 600, 'MAX', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Rajdhani, Arial, sans-serif',
    }).setOrigin(0.5);
    
    // Drag handler for slider
    this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Arc, dragX: number) => {
      if (gameObject === this.buyinSliderHandle && this.buyinPopupGroup.visible) {
        // Constrain to slider bounds
        const minX = 760;
        const maxX = 1160;
        const clampedX = Phaser.Math.Clamp(dragX, minX, maxX);
        this.buyinSliderHandle.x = clampedX;
        
        // Update fill
        this.buyinSliderFill.width = clampedX - minX;
        
        // Calculate buy-in amount
        const percentage = (clampedX - minX) / (maxX - minX);
        this.currentBuyinAmount = Math.round(this.minBuyin + percentage * (this.maxBuyin - this.minBuyin));
        this.buyinAmountText.setText(this.formatBuyinAmount(this.currentBuyinAmount));
      }
    });
    
    // Confirm button (SIĄDŹ)
    const confirmBtn = this.add.image(850, 670, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.5);
    const confirmBtnText = this.add.text(850, 670, 'SIĄDŹ', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Cancel button (ANULUJ)
    const cancelBtn = this.add.image(1070, 670, 'button_small').setInteractive({ useHandCursor: true }).setScale(0.5);
    const cancelBtnText = this.add.text(1070, 670, 'ANULUJ', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Rajdhani, Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    
    // Hover effects
    confirmBtn.on('pointerover', () => confirmBtn.setTint(0xcccccc));
    confirmBtn.on('pointerout', () => confirmBtn.clearTint());
    cancelBtn.on('pointerover', () => cancelBtn.setTint(0xcccccc));
    cancelBtn.on('pointerout', () => cancelBtn.clearTint());
    
    // Button actions
    confirmBtn.on('pointerdown', () => this.confirmBuyin());
    cancelBtn.on('pointerdown', () => this.cancelBuyin());
    
    this.buyinPopupGroup.add([
      overlay, popupBg, popupTitle, this.buyinSubtitleText, this.buyinAmountText,
      sliderTrackBg, this.buyinSliderFill, this.buyinSliderHandle,
      this.buyinMinLabel, this.buyinMaxLabel,
      confirmBtn, confirmBtnText, cancelBtn, cancelBtnText
    ]);
  }

  /**
   * Show buy-in popup with table's min/max values
   */
  private showBuyinPopup(): void {
    if (!this.tableState || this.buyinPopupShown) return;
    
    this.buyinPopupShown = true;
    
    const isLoosePlay = this.isLoosePlayTable();

    // Get table min/max buy-in
    this.minBuyin = this.tableState.minBuyin || 100;
    const tableMaxBuyin = this.tableState.maxBuyin || 1000;

    if (isLoosePlay) {
      this.maxBuyin = tableMaxBuyin;
    } else {
      // Get player's PC balance
      const client = (window as any).pokerClient;
      const playerBalance = client?.coinBalance || 0;

      // Cap max buy-in to player's balance if it's less than table max
      this.maxBuyin = Math.min(tableMaxBuyin, playerBalance);
    }
    
    // Ensure min is not greater than max
    if (this.minBuyin > this.maxBuyin) {
      this.statusText.setText('Niewystarczające środki do gry przy tym stole!').setColor('#ff6b6b');
      this.buyinPopupShown = false;
      return;
    }
    
    // Set default to middle value
    this.currentBuyinAmount = Math.round((this.minBuyin + this.maxBuyin) / 2);
    this.buyinAmountText.setText(this.formatBuyinAmount(this.currentBuyinAmount));

    // Update subtitle with range info
    const buyinUnitLabel = this.getBuyinUnitLabel();
    this.buyinSubtitleText.setText(`Zakres: ${this.minBuyin} - ${this.maxBuyin}${buyinUnitLabel ? ` ${buyinUnitLabel}` : ''}`);
    
    // Position handle at middle
    const percentage = (this.currentBuyinAmount - this.minBuyin) / (this.maxBuyin - this.minBuyin);
    const handleX = 760 + percentage * 400;
    this.buyinSliderHandle.x = handleX;
    
    // Update fill
    this.buyinSliderFill.width = handleX - 760;
    
    // Update min/max labels with actual values
    this.buyinMinLabel.setText(`${this.minBuyin}`);
    this.buyinMaxLabel.setText(`${this.maxBuyin}`);
    
    this.buyinPopupGroup.setVisible(true);
  }

  /**
   * Confirm buy-in and sit down at table
   */
  private async confirmBuyin(): Promise<void> {
    this.buyinPopupGroup.setVisible(false);
    this.statusText.setText('Dołączanie do stołu...').setColor('#ffd93d');

    // SFX: sitting down / buy-in
    this.playSfxByPrefixes(['chips-stack'], { volume: 0.16, rateLimitKey: 'buyin', minIntervalMs: 800 });
    
    try {
      // First leave current table (to reset state)
      const leaveResult = await pokerClient.leaveTable();
      if (!leaveResult.success) {
        this.statusText.setText('Nie można teraz opuścić stołu. Spróbuj ponownie po zakończeniu rozdania.')
          .setColor('#ff6b6b').setVisible(true);
        this.buyinPopupShown = false;
        return;
      }
      
      // Join with selected buy-in
      const result = await pokerClient.joinTable({
        tableId: this.tableState?.tableId || pokerClient.tableId,
        buyin: this.currentBuyinAmount,
        username: pokerClient.username,
        avatar: getSelectedAvatarValue(loadAvatarSettings()),
      });
      
      if (result.success) {
        this.hasSatDown = true;
        this.statusText.setText(`Dołączono z ${this.formatBuyinAmount(this.currentBuyinAmount)}!`).setColor('#6bcf7f');
        this.time.delayedCall(2000, () => this.statusText.setText(''));
        
        // Refresh table state
        this.refreshTableState();
      } else {
        this.statusText.setText(`Błąd: ${result.error}`).setColor('#ff6b6b');
        this.buyinPopupShown = false; // Allow showing popup again
      }
    } catch (error) {
      console.error('Buy-in error:', error);
      this.statusText.setText('Błąd dołączania do stołu').setColor('#ff6b6b');
      this.buyinPopupShown = false;
    }
  }

  /**
   * Cancel buy-in and return to menu
   */
  private async cancelBuyin(): Promise<void> {
    this.buyinPopupGroup.setVisible(false);
    this.statusText.setText('Anulowano. Wracam do menu...').setColor('#888888');
    
    // Leave table and return to menu
    if (pokerClient.tableId) {
      const result = await pokerClient.leaveTable();
      if (!result.success) {
        this.statusText.setText('Nie można teraz opuścić stołu. Spróbuj ponownie po zakończeniu rozdania.')
          .setColor('#ff6b6b').setVisible(true);
        return;
      }
    }
    
    this.time.delayedCall(1000, () => {
      this.scene.start('MenuScene');
    });
  }

  // Clean up when scene is shut down
  shutdown(): void {
    // Stop timers
    if (this.turnTimer) {
      this.turnTimer.destroy();
      this.turnTimer = null;
    }
    
    // Stop polling
    this.stopPolling();
    
    // Clear event handlers to prevent memory leaks
    pokerClient.onLog = null;
    pokerClient.onError = null;
    pokerClient.onPokerSettlementError = null;
    pokerClient.onTableStateChanged = null;
    pokerClient.onHandStarted = null;
    pokerClient.onShowdown = null;
    pokerClient.onHandWinner = null;
    pokerClient.onPlayerAction = null;
    pokerClient.onPlayerJoined = null;
    pokerClient.onPlayerLeft = null;
    pokerClient.onEmojiReceived = null;
    // Keep onDisconnected as it's needed globally

    // Remove keyboard handler
    this.input.keyboard?.off('keydown-ENTER', this.onEnterKey, this);
    this.unsubscribeAudioSettings?.();
    this.unsubscribeAudioSettings = undefined;
  }

  update(): void {
    // Game loop updates if needed
  }
}
